import { extractCheckoutFrequentFlyers, toSeatsLoyaltyProgramNumbers } from 'checkout/lib/utils/flights/frequentFlyer'
import {
  FlightsFareTypes,
  FlightSortOptions,
  FlightViewTypes,
  getAdultPassengerState,
  getChildPassengerState,
  getInfantPassengerState,
  maxFlightInfantAge,
  maxFlightPassengerCount,
  FlightsClassTypes,
  getFlightTitleLabels,
  FlightSegment,
  AIRLINE_CODE_APG,
  AIRLINE_CODE_NORSE_ATLANTIC_UK,
} from 'constants/flight'
import config from 'constants/config'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { createSelector } from 'reselect'
import moment from 'moment'
import qs from 'qs'
import type { SortField } from 'lib/array/arrayUtils'
import type { BaggageAvailabilityTraveler, FlightSeatTraveler } from 'api/flights'
import { groupBy, last, min, nonNullable, sortByAll, sum, arrayToObject, max, without, arrayToMap, uniqueBy } from 'lib/array/arrayUtils'
import switchFunc from 'lib/function/switchFunc'
import type { FlightDropdownOption } from 'components/Flights/types'
import { contains } from 'lib/string/stringUtils'
import { ISO_DATE_FORMAT, SHORT_TIME_FORMAT_24_HOUR } from 'constants/dateFormats'
import { formatShortDateRange } from 'lib/datetime/formatShortDateRange'
import type { FlightSearchParams } from 'hooks/useSearchFlights'
import getObjectKey from 'lib/object/getObjectKey'
import type { FlightSearchWidgetFlightItem } from 'contexts/Flights/FlightSearchWidget/types'
import { isFlightItem, isInsuranceBookingProtectionItem } from 'lib/checkout/checkoutUtils'
import * as isomorphicLocalStorage from 'lib/storage/isomorphicLocalStorage'
import { FLIGHT_INSURANCE_ID_PREFIX } from 'lib/insurance/insuranceFlights'
import { standaloneFlightsFiltersInitialState } from 'lib/flights/standaloneFlightsFiltersInitialState'
import type { FlightsFiltersValues } from './flightFilterTypes'
import { FlightsFiltersRecommendedType } from './flightFilterTypes'
import { tryDecodeFilterPanelSet } from 'components/Common/FilterPanel/filterPanelSetUtils'
import { createElement } from 'react'
import BodyText from 'LuxKit/Typography/BodyText'
import type { IntlShape, MessageDescriptor } from 'react-intl'
import { defineMessages } from 'react-intl'

// fareClassEconomy is an economy fare class
export const fareClassEconomy = 'Economy'
// fareClassBusiness is a business fare class
export const fareClassBusiness = 'Business'
export const fareClassFirst = 'First'
export const fareClassPremium = 'Premium'

const messages = defineMessages({
  fareClassBusiness: {
    id: '7GQN8Mb-j7bt5SQRF-rq',
    defaultMessage: 'Business',
    description: 'Fare class label for business cabin in the cruise cabin selection area',
  },
  fareClassFirst: {
    id: 'OGufkkr5tvOeBRia-xjg',
    defaultMessage: 'First',
    description: 'Fare class label for first class cabin in the cruise cabin selection area',
  },
  fareClassPremium: {
    id: 'EYSZt0dUo2qhzuJNPDyJ',
    defaultMessage: 'Premium',
    description: 'Fare class label for premium cabin in the cruise cabin selection area',
  },
  fareClassEconomy: {
    id: 'FWYXpKg9-MRZsoD22nOH',
    defaultMessage: 'Economy',
    description: 'Fare class label for economy cabin in the cruise cabin selection area',
  },
})

const formatMessage = (
  intl: IntlShape,
  descriptor: MessageDescriptor,
) => intl.formatMessage(descriptor)

export type FareClassKey = typeof fareClassBusiness | typeof fareClassFirst | typeof fareClassPremium | typeof fareClassEconomy

// compareFareClasses is a comparison function to compare two fare classes using a total order
// implemented by the fare class.
function compareFareClasses(a: string, b: string) {
  if (a === b) {
    return 0
  }

  if (a === fareClassEconomy && b !== fareClassEconomy) {
    return -1
  }

  return 1
}

function getHiddenStopCount(flights: Array<{ hiddenStops?: Array<unknown> }>) {
  return flights.reduce((sum, f) => sum + (f.hiddenStops?.length ?? 0), 0)
}

export function getTotalStopCount(flights: Array<{ hiddenStops?: Array<unknown> }>) {
  return flights.length - 1 + getHiddenStopCount(flights)
}

export function getDisplayInfoV2(flights: Array<App.FlightV2>) {
  let bestFareClass = fareClassEconomy
  const fareBookingClasses: Record<string, boolean> = {}
  const flightsWithFreeCheckInBaggage: Record<string, boolean> = {}
  let allFlightsHaveTheSameBaggage = true

  const baggageDetails = {
    freeCarryOnBaggageDescription: flights[0].includedCarryOnBaggage?.description,
    freeCheckedInBaggageDescription: flights[0].includedCheckedBaggage?.description,
    freeCheckedInBaggageIncluded: !!flights[0].includedCheckedBaggage,
  }

  for (let i = 0; i < flights.length; i++) {
    const flight = flights[i]

    fareBookingClasses[flight.bookingClass] = true

    if (flight.includedCheckedBaggage) {
      flightsWithFreeCheckInBaggage[i] = true
    }

    if (allFlightsHaveTheSameBaggage && i > 0) {
      if (baggageDetails.freeCarryOnBaggageDescription !== flight.includedCarryOnBaggage?.description ||
        baggageDetails.freeCheckedInBaggageDescription !== flight.includedCheckedBaggage?.description ||
        baggageDetails.freeCheckedInBaggageIncluded !== !!flight.includedCheckedBaggage
      ) {
        allFlightsHaveTheSameBaggage = false
      }
    }

    if (compareFareClasses(flight.fareClass, bestFareClass) > 0) {
      bestFareClass = flight.fareClass
    }
  }

  return {
    hasMultipleFareBookingClasses: Object.keys(fareBookingClasses).length > 1,
    bestFareClass,
    baggageIncluded: Object.keys(flightsWithFreeCheckInBaggage).length === flights.length,
    allFlightsHaveTheSameBaggage,
  }
}

function applyJourneyFilters<T extends App.AnyJourney>(journeys: Array<T>, filters: Array<App.FlightFilter>) {
  return journeys.map(journey => {
    const isHiddenByFilters = !filters.every(filter => {
      const activeOptions = filter.options.filter(option => option.active)
      const isAllOptionActive = activeOptions.find(ao => ao.value === 'all')
      if (isAllOptionActive) {
        return true
      }
      return activeOptions.some(af => filter.fn(journey, af.value))
    })
    return ({
      ...journey,
      hidden: isHiddenByFilters,
    })
  })
}

function getFlightLuxPlusMemberPrice(fare: App.JourneyV2, useRoundTripPrice?: boolean): number | undefined {
  if (useRoundTripPrice) {
    return fare.price.all.totalRoundTripPrice ?? fare.price.all.totalFare
  }

  return fare.price.all.totalFare
}

function getFlightLuxPlusRegularPrice(fare: App.JourneyV2, useRoundTripPrice?: boolean): number | undefined {
  if (!fare.regularPrice) {
    return undefined
  }

  if (useRoundTripPrice) {
    return fare.regularPrice.all.totalRoundTripPrice ?? fare.regularPrice.all.totalFare
  }

  return fare.regularPrice.all.totalFare
}

function calculateFlightLuxPlusFareSaving(fare: App.JourneyV2, useRoundTripPrice?: boolean): number {
  const memberPrice = getFlightLuxPlusMemberPrice(fare, useRoundTripPrice)
  const regularPrice = getFlightLuxPlusRegularPrice(fare, useRoundTripPrice)
  if (memberPrice === undefined || regularPrice === undefined) {
    return 0
  }

  return Math.max(regularPrice - memberPrice, 0)
}

function hasRoundTripLuxPlusSaving(fare: App.JourneyV2): boolean {
  return Boolean(
    fare.bookingInfo.isLuxPlusFare &&
    fare.price.all.totalRoundTripPrice !== undefined &&
    fare.regularPrice?.all.totalRoundTripPrice !== undefined,
  )
}

function getCheapestLuxPlusFare(fares: Array<App.JourneyV2>, useRoundTripPrice?: boolean): App.JourneyV2 {
  return fares.reduce((cheapest, fare) => {
    const farePrice = getFlightLuxPlusMemberPrice(fare, useRoundTripPrice)
    const cheapestPrice = getFlightLuxPlusMemberPrice(cheapest, useRoundTripPrice)
    if (farePrice !== undefined && (cheapestPrice === undefined || farePrice < cheapestPrice)) {
      return fare
    }

    return cheapest
  })
}

// Savings shown in the LuxPlus+ filter banner. For return fares, the first bound
// already includes the best paired return leg in totalRoundTripPrice.
export function calculateFlightLuxPlusMemberSavings(bounds: Array<Array<App.JourneyV2>>): number {
  const roundTripMemberFares = bounds[0]?.filter(hasRoundTripLuxPlusSaving) ?? []
  if (roundTripMemberFares.length) {
    return calculateFlightLuxPlusFareSaving(getCheapestLuxPlusFare(roundTripMemberFares, true), true)
  }

  return bounds.reduce((total, bound) => {
    const memberFares = bound.filter(fare => fare.bookingInfo.isLuxPlusFare)
    if (!memberFares.length) {
      return total
    }

    return total + calculateFlightLuxPlusFareSaving(getCheapestLuxPlusFare(memberFares))
  }, 0)
}

// Mirrors the LuxPlus+ price render gate on the flight tile (FareFamilyFlightTileV2Price): the
// member price is only rendered when the fare is a LuxPlus+ fare AND a regular price exists to
// strike through. `isLuxPlusFare` on its own over-counts, so don't use it to measure display rate.
export function isLuxPlusPriceDisplayed(fare: App.JourneyV2): boolean {
  return Boolean(fare.bookingInfo?.isLuxPlusFare && fare.regularPrice)
}

// Whether any bound of a search returned a fare that displays a LuxPlus+ price. Deliberately spans
// every bound rather than the active segment, since a return search can surface member pricing on
// the returning leg only.
export function hasAnyLuxPlusPriceDisplayed(bounds: Array<Array<App.JourneyV2>>): boolean {
  return bounds.some(bound => bound?.some(isLuxPlusPriceDisplayed))
}

export interface FlightLuxPlusItemFare {
  journey: App.JourneyV2
  /** Which question the row answers — rows are not interchangeable, so queries must filter on it. */
  selection: 'carrier_deepest' | 'cheapest_member' | 'cheapest_public'
  /** Absent when the fare displays no LuxPlus+ price — never zeroed. */
  memberPricePerAdult?: number
  nonMemberPricePerAdult: number
  priceBasis: 'round_trip' | 'per_bound'
  /** Fares behind a `carrier_deepest` row, so item count and fare count can't silently disagree. */
  memberFareCount?: number
}

// Per-adult, unlike the `.all` accessors above: FLY-5724 measures discount depth per person so that
// searches with different passenger counts are comparable. `price.adult` is already a per-person
// figure (see `FlightsFiltersPriceType.ADULT` in the trip planner flight filters), so it must not be
// divided by `passengerTypeQuantity`.
export function getAdultPrice(price: App.JourneyV2Price | undefined, useRoundTripPrice: boolean): number | undefined {
  if (!price) {
    return undefined
  }

  // No `?? totalFare` backstop on the round-trip path — mixing round-trip and per-bound amounts in
  // one record would make discount % and cheapest-overall comparisons meaningless.
  return useRoundTripPrice ? price.adult.totalRoundTripPrice : price.adult.totalFare
}

// Mirrors `hasRoundTripLuxPlusSaving` (banner) on the per-adult fields used by FLY-5724: only treat a
// fare as round-trip priced when *both* the member and struck-through regular prices carry a
// `totalRoundTripPrice`.
function hasRoundTripLuxPlusAdultPrice(fare: App.JourneyV2): boolean {
  return Boolean(
    isLuxPlusPriceDisplayed(fare) &&
    fare.price.adult.totalRoundTripPrice !== undefined &&
    fare.regularPrice?.adult.totalRoundTripPrice !== undefined,
  )
}

/**
 * Fares FLY-5724 emits as `item` contexts: each carrier's deepest per-adult saving, plus the
 * cheapest member and cheapest public fares. A selection, not a rollup — depth, penetration and the
 * public comparison are all derived in SQL, so the definition can change without a client release.
 *
 * Bounded at ~carrier count + 2. A flat "first N displayed" cap can push the cheapest fare or a
 * whole carrier out of frame.
 *
 * Per adult and deepest-discount, so these won't reconcile with the savings banner (whole-booking,
 * cheapest member fare). Empty when no fare displays a member price, so nothing is attached rather
 * than a $0 saving being implied.
 *
 * @param isRoundTripEligible Whether the search can carry whole-trip totals — true for **both**
 * return and multi-city, false only for one-way. Passing `false` for a multi-city search would price
 * its whole-trip totals as `per_bound`, which is the mislabelling this parameter exists to prevent.
 */
export function deriveFlightLuxPlusItemFares(
  bounds: Array<Array<App.JourneyV2>>,
  { isRoundTripEligible }: { isRoundTripEligible: boolean },
): Array<FlightLuxPlusItemFare> {
  const departing = bounds[0] ?? []

  // Same gate as the LuxPlus+ filter banner: only switch to the round-trip basis when a departing
  // member fare actually carries round-trip totals on both sides. Otherwise pool every bound on the
  // per-bound basis (so a return-only member fare is still counted, matching `hasAnyLuxPlusPriceDisplayed`).
  // Restricting to the departing bound under round-trip is required — `totalRoundTripPrice` already
  // includes the best paired return leg, so pooling the returning bound would double-count.
  //
  // `isRoundTripEligible` is load-bearing, not belt-and-braces: on a one-way search the provider
  // returns `totalRoundTripPrice` equal to `totalFare`, so the field-presence check passes vacuously
  // and labels per-bound amounts as `round_trip`. That mislabelled ~19% of round_trip rows in prod.
  const useRoundTripPrice = isRoundTripEligible && departing.some(hasRoundTripLuxPlusAdultPrice)
  const pool = useRoundTripPrice ? departing : bounds.flatMap(bound => bound ?? [])
  const priceBasis: FlightLuxPlusItemFare['priceBasis'] = useRoundTripPrice ? 'round_trip' : 'per_bound'

  const memberFares = pool.filter(isLuxPlusPriceDisplayed)
  const carrierFares = nonNullable(Array.from(
    groupBy(memberFares, fare => fare.carrier),
    ([, fares]): FlightLuxPlusItemFare | undefined => {
      const priced = nonNullable(fares.map(fare => {
        const memberPricePerAdult = getAdultPrice(fare.price, useRoundTripPrice)
        const nonMemberPricePerAdult = getAdultPrice(fare.regularPrice, useRoundTripPrice)
        // Both are optional on `PriceBreakdown`. Skip rather than coerce to 0, which would invent a
        // 100% discount. Under round-trip this also drops fares missing `totalRoundTripPrice`.
        if (memberPricePerAdult === undefined || nonMemberPricePerAdult === undefined) {
          return undefined
        }

        return { fare, memberPricePerAdult, nonMemberPricePerAdult }
      }))

      const deepest = max(priced, ({ memberPricePerAdult, nonMemberPricePerAdult }) => nonMemberPricePerAdult - memberPricePerAdult)
      if (!deepest) {
        return undefined
      }

      return {
        journey: deepest.fare,
        selection: 'carrier_deepest',
        memberPricePerAdult: deepest.memberPricePerAdult,
        nonMemberPricePerAdult: deepest.nonMemberPricePerAdult,
        priceBasis,
        memberFareCount: priced.length,
      }
    },
  ))

  if (!carrierFares.length) {
    return []
  }

  // Not implied by the carrier picks: those are deepest-discount, so a cheaper member fare with a
  // shallower discount loses. Without this row the cheapest member fare can be absent entirely.
  const pricedMemberFares = nonNullable(memberFares.map(fare => {
    const memberPricePerAdult = getAdultPrice(fare.price, useRoundTripPrice)
    const nonMemberPricePerAdult = getAdultPrice(fare.regularPrice, useRoundTripPrice)
    if (memberPricePerAdult === undefined || nonMemberPricePerAdult === undefined) {
      return undefined
    }

    return { fare, memberPricePerAdult, nonMemberPricePerAdult }
  }))
  const cheapestMember = min(pricedMemberFares, ({ memberPricePerAdult }) => memberPricePerAdult)
  // Skipped when already a carrier pick, so no fare is counted twice.
  const cheapestMemberIsNew = cheapestMember && !carrierFares.some(({ journey }) => journey === cheapestMember.fare)

  // So "did membership actually beat the public price?" is answerable from the emitted rows alone.
  const publicFares = nonNullable(pool
    .filter(fare => !isLuxPlusPriceDisplayed(fare))
    .map(fare => {
      const nonMemberPricePerAdult = getAdultPrice(fare.price, useRoundTripPrice)
      return nonMemberPricePerAdult === undefined ? undefined : { fare, nonMemberPricePerAdult }
    }))
  const cheapestPublic = min(publicFares, ({ nonMemberPricePerAdult }) => nonMemberPricePerAdult)

  return [
    ...carrierFares,
    ...(cheapestMemberIsNew ? [{
      journey: cheapestMember.fare,
      selection: 'cheapest_member' as const,
      memberPricePerAdult: cheapestMember.memberPricePerAdult,
      nonMemberPricePerAdult: cheapestMember.nonMemberPricePerAdult,
      priceBasis,
    }] : []),
    ...(cheapestPublic ? [{
      journey: cheapestPublic.fare,
      selection: 'cheapest_public' as const,
      nonMemberPricePerAdult: cheapestPublic.nonMemberPricePerAdult,
      priceBasis,
    }] : []),
  ]
}

export const getJourneys = {
  departing: createSelector(
    (state: App.State) => state.flights.journeys,
    (state: App.State) => state.flights.flightSearchV2?.onewayFares,
    (journeys, oneWayFares): Array<App.AnyJourney> => oneWayFares?.[0] ?? journeys,
  ),
  returning: createSelector(
    (state: App.State) => state.flights.journeys,
    (state: App.State) => state.flights.flightSearchV2?.onewayFares,
    (journeys, oneWayFares): Array<App.AnyJourney> => oneWayFares?.[1] ?? journeys,
  ),
}

const getFilteredDepartingJourneys = createSelector(
  (state: App.State) => getJourneys.departing(state),
  (state: App.State) => state.flights.filters,
  (journeys, filters) => applyJourneyFilters(journeys, filters),
)

const flightPriceSort: SortField<App.Journey> = { selector: j => j.cost, direction: 'asc' }

const departingFlightStopoverSort: SortField<App.Journey> = { selector: j => j.departing.flights.length, direction: 'asc' }
const departingFlightTimeSort: SortField<App.Journey> = { selector: j => j.departing.flightTime ? convertTimeObjectToMinutes(j.departing.flightTime) : undefined, direction: 'asc' }
const departingDepartureSort: SortField<App.Journey> = { selector: j => +new Date(`${j.departing.flights[0].departingDate}T${j.departing.flights[0].departingTime}`), direction: 'asc' }
const departingDepartureDescSort: SortField<App.Journey> = { selector: j => +new Date(`${j.departing.flights[0].departingDate}T${j.departing.flights[0].departingTime}`), direction: 'desc' }
const departingArrivalSort: SortField<App.Journey> = { selector: j => +new Date(`${last(j.departing.flights).arrivalDate}T${last(j.departing.flights).arrivalTime}`), direction: 'asc' }
const departingArrivalDescSort: SortField<App.Journey> = { selector: j => +new Date(`${last(j.departing.flights).arrivalDate}T${last(j.departing.flights).arrivalTime}`), direction: 'desc' }

const returningFlightStopoverSort: SortField<App.Journey> = { selector: j => j.returning?.flights.length, direction: 'asc' }
const returningFlightTimeSort: SortField<App.Journey> = { selector: j => j.returning?.flightTime ? convertTimeObjectToMinutes(j.returning.flightTime) : undefined, direction: 'asc' }
const returningDepartureSort: SortField<App.Journey> = { selector: j => j.returning ? +new Date(`${j.returning.flights[0].departingDate}T${j.returning.flights[0].departingTime}`) : undefined, direction: 'asc' }
const returningDepartureDescSort: SortField<App.Journey> = { selector: j => j.returning ? +new Date(`${j.returning.flights[0].departingDate}T${j.returning.flights[0].departingTime}`) : undefined, direction: 'desc' }
const returningArrivalSort: SortField<App.Journey> = { selector: j => j.returning ? +new Date(`${last(j.returning.flights).arrivalDate}T${last(j.returning.flights).arrivalTime}`) : undefined, direction: 'asc' }
const returningArrivalDescSort: SortField<App.Journey> = { selector: j => j.returning ? +new Date(`${last(j.returning.flights).arrivalDate}T${last(j.returning.flights).arrivalTime}`) : undefined, direction: 'desc' }

export const getDepartingSortList = switchFunc<Array<SortField<App.Journey>>>({
  [FlightSortOptions.Best]: [departingFlightStopoverSort, flightPriceSort, departingFlightTimeSort, departingDepartureSort],
  [FlightSortOptions.Cheapest]: [flightPriceSort, departingFlightStopoverSort, departingFlightTimeSort, departingDepartureSort],
  [FlightSortOptions.Fastest]: [departingFlightTimeSort, flightPriceSort, departingDepartureSort, departingFlightTimeSort],
  [FlightSortOptions.EarliestTakeOff]: [departingDepartureSort, flightPriceSort, departingFlightStopoverSort, departingFlightTimeSort],
  [FlightSortOptions.LatestTakeOff]: [departingDepartureDescSort, flightPriceSort, departingFlightStopoverSort, departingFlightTimeSort],
  [FlightSortOptions.EarliestLanding]: [departingArrivalSort, flightPriceSort, departingFlightStopoverSort, departingFlightTimeSort],
  [FlightSortOptions.LatestLanding]: [departingArrivalDescSort, flightPriceSort, departingFlightStopoverSort, departingFlightTimeSort],
}, [flightPriceSort, departingFlightStopoverSort, departingFlightTimeSort, departingDepartureSort, departingDepartureDescSort, departingArrivalSort, departingArrivalDescSort])

// Get the price that is used for sorting flight journeys
export function getJourneySortPrice(journey: App.JourneyV2, price = journey.price): number {
  if (journey.fareType === FlightViewTypes.RETURN) {
    return price.all?.totalRoundTripPrice ?? price.all?.totalFare ?? price.adult?.totalFare ?? 0
  }
  return price.all?.totalFare ?? price.adult?.totalFare ?? 0
}

const flightPriceSortV2: SortField<App.JourneyV2> = {
  selector: getJourneySortPrice,
  direction: 'asc',
}

function getFlightDepartureTimestampV2(flight?: App.FlightV2) {
  if (!flight) return undefined
  if (flight.departingDate && flight.departingTime) {
    return +new Date(`${flight.departingDate}T${flight.departingTime}`)
  }
  return flight.departureDateTime ? +new Date(flight.departureDateTime) : undefined
}

function getFlightArrivalTimestampV2(flight?: App.FlightV2) {
  if (!flight) return undefined
  if (flight.arrivalDate && flight.arrivalTime) {
    return +new Date(`${flight.arrivalDate}T${flight.arrivalTime}`)
  }
  return flight.arrivalDateTime ? +new Date(flight.arrivalDateTime) : undefined
}

const departingFlightStopoverSortV2: SortField<App.JourneyV2> = { selector: j => j.flightGroup.numberOfStops, direction: 'asc' }
const departingFlightTimeSortV2: SortField<App.JourneyV2> = { selector: j => convertTimeObjectToMinutes(j.flightGroup.totalFlightDuration), direction: 'asc' }
const departingDepartureSortV2: SortField<App.JourneyV2> = { selector: j => getFlightDepartureTimestampV2(j.flightGroup.flights[0]), direction: 'asc' }
const departingDepartureDescSortV2: SortField<App.JourneyV2> = { selector: j => getFlightDepartureTimestampV2(j.flightGroup.flights[0]), direction: 'desc' }
const departingArrivalSortV2: SortField<App.JourneyV2> = { selector: j => getFlightArrivalTimestampV2(last(j.flightGroup.flights)), direction: 'asc' }
const departingArrivalDescSortV2: SortField<App.JourneyV2> = { selector: j => getFlightArrivalTimestampV2(last(j.flightGroup.flights)), direction: 'desc' }
const luxPlusFareSort: SortField<App.JourneyV2> = { selector: j => Boolean(j.bookingInfo.isLuxPlusFare), direction: 'desc' }

const returningFlightStopoverSortV2: SortField<App.JourneyV2> = { selector: j => j.flightGroup.numberOfStops, direction: 'asc' }
const returningFlightTimeSortV2: SortField<App.JourneyV2> = { selector: j => convertTimeObjectToMinutes(j.flightGroup.totalFlightDuration), direction: 'asc' }
const returningDepartureSortV2: SortField<App.JourneyV2> = { selector: j => getFlightDepartureTimestampV2(j.flightGroup.flights[0]), direction: 'asc' }
const returningDepartureDescSortV2: SortField<App.JourneyV2> = { selector: j => getFlightDepartureTimestampV2(j.flightGroup.flights[0]), direction: 'desc' }
const returningArrivalSortV2: SortField<App.JourneyV2> = { selector: j => getFlightArrivalTimestampV2(last(j.flightGroup.flights)), direction: 'asc' }
const returningArrivalDescSortV2: SortField<App.JourneyV2> = { selector: j => getFlightArrivalTimestampV2(last(j.flightGroup.flights)), direction: 'desc' }

export function getCombinedJourneyPrice(combinedJourney: App.FlightCombinedJourney) {
  return (combinedJourney.departingJourney.price.all?.totalFare ?? combinedJourney.departingJourney.price.adult?.totalFare ?? 0) +
    (combinedJourney.returningJourney.price.all?.totalFare ?? combinedJourney.returningJourney.price.adult?.totalFare ?? 0)
}

export function getCombinedJourneyDuration(combinedJourney: App.FlightCombinedJourney) {
  return convertTimeObjectToMinutes(combinedJourney.departingJourney.flightGroup.totalFlightDuration) +
    convertTimeObjectToMinutes(combinedJourney.returningJourney.flightGroup.totalFlightDuration)
}

export function getCombinedJourneyAverageDuration(combinedJourney: App.FlightCombinedJourney) {
  const averageDuration = Math.round(getCombinedJourneyDuration(combinedJourney) / 2)

  return {
    hours: Math.floor(averageDuration / 60),
    minutes: averageDuration % 60,
  }
}

function getCombinedJourneyFlightCount(combinedJourney: App.FlightCombinedJourney) {
  return combinedJourney.departingJourney.flightGroup.flights.length + combinedJourney.returningJourney.flightGroup.flights.length
}

const combinedJourneyFlightCountSort: SortField<App.FlightCombinedJourney> = { selector: getCombinedJourneyFlightCount, direction: 'asc' }
const combinedJourneyPriceSort: SortField<App.FlightCombinedJourney> = { selector: getCombinedJourneyPrice, direction: 'asc' }
const combinedJourneyLuxPlusFareSort: SortField<App.FlightCombinedJourney> = {
  selector: combinedJourney => Boolean(combinedJourney.departingJourney.bookingInfo.isLuxPlusFare || combinedJourney.returningJourney.bookingInfo.isLuxPlusFare),
  direction: 'desc',
}
const combinedJourneyDurationSort: SortField<App.FlightCombinedJourney> = { selector: getCombinedJourneyDuration, direction: 'asc' }
const combinedJourneyDomesticBestSort: SortField<App.FlightCombinedJourney> = { selector: combinedJourney => combinedJourney.domesticBestSortRank ?? Number.POSITIVE_INFINITY, direction: 'asc' }
const combinedJourneyDepartingDepartureSort: SortField<App.FlightCombinedJourney> = { selector: combinedJourney => getFlightDepartureTimestampV2(combinedJourney.departingJourney.flightGroup.flights[0]), direction: 'asc' }
const combinedJourneyDepartingDepartureDescSort: SortField<App.FlightCombinedJourney> = { selector: combinedJourney => getFlightDepartureTimestampV2(combinedJourney.departingJourney.flightGroup.flights[0]), direction: 'desc' }
const combinedJourneyReturningArrivalSort: SortField<App.FlightCombinedJourney> = { selector: combinedJourney => getFlightArrivalTimestampV2(last(combinedJourney.returningJourney.flightGroup.flights)), direction: 'asc' }
const combinedJourneyReturningArrivalDescSort: SortField<App.FlightCombinedJourney> = { selector: combinedJourney => getFlightArrivalTimestampV2(last(combinedJourney.returningJourney.flightGroup.flights)), direction: 'desc' }

export const getDepartingSortListV2 = switchFunc<Array<SortField<App.JourneyV2>>>({
  [FlightSortOptions.Best]: [departingFlightStopoverSortV2, flightPriceSortV2, luxPlusFareSort, departingFlightTimeSortV2, departingDepartureSortV2],
  [FlightSortOptions.Cheapest]: [flightPriceSortV2, departingFlightStopoverSortV2, departingFlightTimeSortV2, departingDepartureSortV2],
  [FlightSortOptions.Fastest]: [departingFlightTimeSortV2, flightPriceSortV2, departingDepartureSortV2, departingFlightTimeSortV2],
  [FlightSortOptions.EarliestTakeOff]: [departingDepartureSortV2, flightPriceSortV2, departingFlightStopoverSortV2, departingFlightTimeSortV2],
  [FlightSortOptions.LatestTakeOff]: [departingDepartureDescSortV2, flightPriceSortV2, departingFlightStopoverSortV2, departingFlightTimeSortV2],
  [FlightSortOptions.EarliestLanding]: [departingArrivalSortV2, flightPriceSortV2, departingFlightStopoverSortV2, departingFlightTimeSortV2],
  [FlightSortOptions.LatestLanding]: [departingArrivalDescSortV2, flightPriceSortV2, departingFlightStopoverSortV2, departingFlightTimeSortV2],
}, [flightPriceSortV2, departingFlightStopoverSortV2, departingFlightTimeSortV2, departingDepartureSortV2, departingDepartureDescSortV2, departingArrivalSortV2, departingArrivalDescSortV2, luxPlusFareSort])

export const getReturningSortListV2 = switchFunc<Array<SortField<App.JourneyV2>>>({
  [FlightSortOptions.Best]: [returningFlightStopoverSortV2, flightPriceSortV2, luxPlusFareSort, returningFlightTimeSortV2, returningDepartureSortV2],
  [FlightSortOptions.Cheapest]: [flightPriceSortV2, returningFlightStopoverSortV2, returningFlightTimeSortV2, returningDepartureSortV2],
  [FlightSortOptions.Fastest]: [returningFlightTimeSortV2, flightPriceSortV2, returningDepartureSortV2, returningFlightTimeSortV2],
  [FlightSortOptions.EarliestTakeOff]: [returningDepartureSortV2, flightPriceSortV2, returningFlightStopoverSortV2, returningFlightTimeSortV2],
  [FlightSortOptions.LatestTakeOff]: [returningDepartureDescSortV2, flightPriceSortV2, returningFlightStopoverSortV2, returningFlightTimeSortV2],
  [FlightSortOptions.EarliestLanding]: [returningArrivalSortV2, flightPriceSortV2, returningFlightStopoverSortV2, returningFlightTimeSortV2],
  [FlightSortOptions.LatestLanding]: [returningArrivalDescSortV2, flightPriceSortV2, returningFlightStopoverSortV2, returningFlightTimeSortV2],
}, [flightPriceSortV2, returningFlightStopoverSortV2, returningFlightTimeSortV2, returningDepartureSortV2, returningDepartureDescSortV2, returningArrivalSortV2, returningArrivalDescSortV2, luxPlusFareSort])

export const getCombinedJourneySortList = switchFunc<Array<SortField<App.FlightCombinedJourney>>>({
  [FlightSortOptions.Best]: [combinedJourneyDomesticBestSort, combinedJourneyFlightCountSort, combinedJourneyPriceSort, combinedJourneyLuxPlusFareSort, combinedJourneyDurationSort, combinedJourneyDepartingDepartureSort],
  [FlightSortOptions.Cheapest]: [combinedJourneyPriceSort, combinedJourneyFlightCountSort, combinedJourneyDurationSort, combinedJourneyDepartingDepartureSort],
  [FlightSortOptions.Fastest]: [combinedJourneyDurationSort, combinedJourneyPriceSort, combinedJourneyDepartingDepartureSort],
  [FlightSortOptions.EarliestTakeOff]: [combinedJourneyDepartingDepartureSort, combinedJourneyPriceSort, combinedJourneyFlightCountSort, combinedJourneyDurationSort],
  [FlightSortOptions.LatestTakeOff]: [combinedJourneyDepartingDepartureDescSort, combinedJourneyPriceSort, combinedJourneyFlightCountSort, combinedJourneyDurationSort],
  [FlightSortOptions.EarliestLanding]: [combinedJourneyReturningArrivalSort, combinedJourneyPriceSort, combinedJourneyFlightCountSort, combinedJourneyDurationSort],
  [FlightSortOptions.LatestLanding]: [combinedJourneyReturningArrivalDescSort, combinedJourneyPriceSort, combinedJourneyFlightCountSort, combinedJourneyDurationSort],
}, [combinedJourneyDomesticBestSort, combinedJourneyPriceSort, combinedJourneyFlightCountSort, combinedJourneyLuxPlusFareSort, combinedJourneyDurationSort, combinedJourneyDepartingDepartureSort, combinedJourneyDepartingDepartureDescSort, combinedJourneyReturningArrivalSort, combinedJourneyReturningArrivalDescSort])

export function getDefaultFlightSort(regionCode: string): FlightSortOptions {
  return regionCode === 'GB' ? FlightSortOptions.Cheapest : FlightSortOptions.Best
}

export function getJourneysSortList(option: FlightSortOptions, segment: 'departing' | 'returning', isV2?: boolean) {
  if (segment === 'departing') {
    return isV2 ? getDepartingSortListV2(option) : getDepartingSortList(option)
  } else {
    return isV2 ? getReturningSortListV2(option) : getReturningSortList(option)
  }
}

export function getUniqueDepartingFlights(journeys: Array<App.Journey>) {
  const grouped = groupBy(journeys, j => j.departing.journeyKey)
  return Array.from(grouped.values()).map(group => {
    const visibleJourneys = group.filter(j => !j.hidden)
    return min(visibleJourneys.length === 0 ? group : visibleJourneys, val => val.cost)
  })
}

// getDepartingJourneys returns an array of journeys with unique departing journey key.
// Concretely, it de-duplicates journeys by its 'departing' object's 'journeyKey'. Any tie is
// broken by choosing the object from the journey with a lower cost.
export const getDepartingJourneys = createSelector(
  (state: App.State) => getFilteredDepartingJourneys(state),
  (state: App.State) => state.flights.sort,
  (journeys: Array<App.AnyJourney>, sortValue) => {
    if (isJourneyV2Items(journeys)) {
      const visibleJourneys = journeys.filter((journey) => !journey.hidden)
      return sortByAll(nonNullable(visibleJourneys), getDepartingSortListV2(sortValue))
    }
    const uniqueJourneys = getUniqueDepartingFlights(journeys as Array<App.Journey>)

    return sortByAll(nonNullable(uniqueJourneys), getDepartingSortList(sortValue))
  },
)

export const getReturningSortList = switchFunc<Array<SortField<App.Journey>>>({
  [FlightSortOptions.Best]: [returningFlightStopoverSort, flightPriceSort, returningFlightTimeSort, returningDepartureSort],
  [FlightSortOptions.Cheapest]: [flightPriceSort, returningFlightStopoverSort, returningFlightTimeSort, returningDepartureSort],
  [FlightSortOptions.Fastest]: [returningFlightTimeSort, flightPriceSort, returningDepartureSort, returningFlightTimeSort],
  [FlightSortOptions.EarliestTakeOff]: [returningDepartureSort, flightPriceSort, returningFlightStopoverSort, returningFlightTimeSort],
  [FlightSortOptions.LatestTakeOff]: [returningDepartureDescSort, flightPriceSort, returningFlightStopoverSort, returningFlightTimeSort],
  [FlightSortOptions.EarliestLanding]: [returningArrivalSort, flightPriceSort, returningFlightStopoverSort, returningFlightTimeSort],
  [FlightSortOptions.LatestLanding]: [returningArrivalDescSort, flightPriceSort, returningFlightStopoverSort, returningFlightTimeSort],
}, [flightPriceSort, returningFlightStopoverSort, returningFlightTimeSort, returningDepartureSort, returningDepartureDescSort, returningArrivalSort, returningArrivalDescSort])

export function tripType(journey: App.Journey) {
  return journey.departing.flights[0].departingDisplayNames.departingCountry === journey.returning?.flights[0].departingDisplayNames.departingCountry ?
    'domestic' : 'international'
}

export function getCharterFeederSort(
  charterBundleRoute: App.CharterBundleRoute | undefined,
  segment: FlightSegment,
  currentSort: FlightSortOptions,
  isOneWayInternationalSelection = false,
): FlightSortOptions {
  const isMaldivesCharter = charterBundleRoute === 'melbourne-maldives'
  const isDefaultSort = currentSort === FlightSortOptions.Best

  if (isMaldivesCharter && isDefaultSort && isOneWayInternationalSelection) {
    return FlightSortOptions.Cheapest
  }

  if (isMaldivesCharter && isDefaultSort) {
    return segment === FlightSegment.DEPARTING
      ? FlightSortOptions.LatestTakeOff
      : FlightSortOptions.EarliestTakeOff
  }

  return currentSort
}

const createAdult = (index: number): App.Passenger => {
  const passenger = getAdultPassengerState()
  return {
    id: `adult-${index}`,
    type: 'adult',
    ...passenger,
  }
}

const createChild = (index: number): App.Passenger => {
  const passenger = getChildPassengerState()
  return {
    id: `child-${index}`,
    type: 'child',
    ...passenger,
  }
}

const createInfant = (index: number): App.Passenger => {
  const passenger = getInfantPassengerState()
  return {
    id: `infant-${index}`,
    type: 'infant',
    ...passenger,
  }
}

/**
 * Build list of pax for the new checkout cart item state
 *
 * @remarks
 * Passenger `id` suffixes start at 1, e.g. 'adult-1'.
 */
export function newCheckoutCreatePassengers({ adults, children = 0, infants = 0 }: App.Occupants) {
  const adultPaxList: Array<App.Passenger> = []
  const childPaxList: Array<App.Passenger> = []
  const infantList: Array<App.Passenger> = []

  for (let i = 0; i < adults; i++) { adultPaxList.push(createAdult(i + 1)) }
  for (let i = 0; i < children; i++) { childPaxList.push(createChild(i + 1)) }
  for (let i = 0; i < infants; i++) { infantList.push(createInfant(i + 1)) }

  return [...adultPaxList, ...childPaxList, ...infantList]
}

export function resetFilter(filter: App.FlightFilter) {
  return {
    ...filter,
    options: filter.options.map(o => ({
      ...o,
      active: !!o.isDefault,
    })),
  }
}

export function getFlightCalendarKey(props: { startDate: string, endDate: string, origin: string, destination: string, nights?: number, region: string, currency: string, tripType?: string, fareClass?: string }) {
  return `${props.startDate}-${props.endDate}-${props.origin}-${props.destination}-${props.nights}-${props.region}-${props.currency}-${props.tripType}-${props.fareClass}`
}

/**
 * Number of alternative dates to fetch on each side of the center date.
 * Used for API requests - always fetches the maximum (desktop) size for caching.
 */
export const FLIGHT_DATE_GRID_ALTERNATIVE_DATES_COUNT = 3
export const FLIGHT_DATE_GRID_ALTERNATIVE_DATES_COUNT_NDC = 2

/**
 * Desktop grid: 7 columns (departure) × 7 rows (return) (Non NDC)
 * ±3 days from center = 7 dates
 */
export const FLIGHT_DATE_GRID_DEPART_ALTERNATIVES_DESKTOP = 3
export const FLIGHT_DATE_GRID_RETURN_ALTERNATIVES_DESKTOP = 3

/**
 * NDC: ±2 days from center = 5 dates
 */
export const FLIGHT_DATE_GRID_DEPART_ALTERNATIVES_DESKTOP_NDC = 2
export const FLIGHT_DATE_GRID_RETURN_ALTERNATIVES_DESKTOP_NDC = 2

/**
 * Mobile grid: 4 columns (departure) × 6 rows (return)
 * Departure: center + 3 days after = 4 dates (asymmetric: 0 before, 3 after)
 * Return: 2 before + center + 3 after = 6 dates (selected date near middle)
 */
export const FLIGHT_DATE_GRID_DEPART_BEFORE_MOBILE = 0
export const FLIGHT_DATE_GRID_DEPART_AFTER_MOBILE = 3
export const FLIGHT_DATE_GRID_RETURN_BEFORE_MOBILE = 2
export const FLIGHT_DATE_GRID_RETURN_AFTER_MOBILE = 2

/**
 * Mobile alternative dates bar: the selected departure date with one date on each side.
 * The API still returns the desktop-sized window so the shared cache can serve navigation.
 */
export const FLIGHT_ALTERNATIVE_DATES_BAR_BEFORE_MOBILE = 1
export const FLIGHT_ALTERNATIVE_DATES_BAR_AFTER_MOBILE = 1

/**
 * A mixed traveller search is any search where the traveller mix includes a child or infant
 * alongside an adult. Adults-only searches (regardless of how many adults) are not mixed.
 *
 * Mixed traveller searches display the total fare for all travellers rather than the per adult
 * fare, since a per adult price understates what the customer actually pays.
 */
export function isMixedTravellerSearch(occupants: { adults: number, children?: number, infants?: number }): boolean {
  return occupants.adults > 0 && ((occupants.children ?? 0) > 0 || (occupants.infants ?? 0) > 0)
}

export function getFlightDateGridDisplayPrice(
  cell: App.FlightDateGridCell,
  occupants: { adults: number, children?: number, infants?: number },
): number | undefined {
  return isMixedTravellerSearch(occupants) ? cell.totalPrice : cell.priceBreakdown.adult?.totalFare
}

/**
 * Whether a standalone flight tile/journey should display and calculate on a per adult basis.
 * Bundled Hotel + Flights always uses the all-travellers basis; standalone only does when the
 * traveller mix is adults-only. Use this (rather than `isStandalone` alone) everywhere a price is
 * picked between `journey.price.adult` and `journey.price.all`, including relative/delta price
 * calculations, so a base price and its delta never mix bases.
 */
export function isPerAdultPriceDisplay(isStandalone: boolean, isMixedTravellerSearch: boolean): boolean {
  return isStandalone && !isMixedTravellerSearch
}

export function getFlightDateGridOccupantsKey(occupants: { adults: number, children?: number, infants?: number }) {
  const a = occupants.adults ?? 0
  const c = occupants.children ?? 0
  const i = occupants.infants ?? 0
  return `${a}adult${c}child${i}infant`
}

// Route-level prefix for filtering cached grid entries
export function getFlightDateGridKeyPrefix(props: { origin: string, destination: string, fareType: string, occupants: { adults: number, children?: number, infants?: number }, fareClass?: string }) {
  return `${props.fareType}-${props.origin}-${props.destination}-${getFlightDateGridOccupantsKey(props.occupants)}${props.fareClass ? `-${props.fareClass}` : ''}`
}

/** Full cache key for a date grid API response, scoped by route + fareType + fareClass + occupants + dates. Used as top-level key when saving API response. */
export function getFlightDateGridKey(props: { departureDate: string, returnDate?: string, origin: string, destination: string, fareType: string, occupants: { adults: number, children?: number, infants?: number }, fareClass?: string }) {
  return `${getFlightDateGridKeyPrefix(props)}-${props.departureDate}-${props.returnDate ?? ''}`
}

export function updateFilter(filter: App.FlightFilter, newOption: App.FlightFilterOption, selectedOptionOnly: boolean) {
  return {
    ...filter,
    options: filter.options.map(o => {
      // If "select only" option select => only that should be select
      // If "all" select => de-select all other options
      // If any other option select => de-select "all" option
      // all option & other option can't select at one time

      if (selectedOptionOnly) {
        return {
          ...o,
          active: o === newOption,
        }
      }

      if (newOption.value === 'all') {
        return {
          ...o,
          active: o === newOption ? !o.active : false,
        }
      }

      if (o.value === 'all') {
        return {
          ...o,
          active: false,
        }
      } else if (o === newOption) {
        return {
          ...o,
          active: !o.active,
        }
      }

      return o
    }),
  }
}

export function getSortingLabel(selectedOption?: FlightSortOptions) {
  const intl = getCurrentIntl()
  switch (selectedOption) {
    case FlightSortOptions.EarliestTakeOff:
      return {
        sortingTitle: intl.formatMessage({
          id: 'wePP2RBlHCrc1MrLyUzU',
          defaultMessage: 'Take-off:',
          description: 'Flight sorting title label for take-off time sorting',
        }),
        sortingLabel: intl.formatMessage({
          id: 'utWoRa-2wCZKoDamMRzC',
          defaultMessage: 'Earliest',
          description: 'Flight sorting label for the earliest take-off time option',
        }),
      }
    case FlightSortOptions.LatestTakeOff:
      return {
        sortingTitle: intl.formatMessage({
          id: 'wePP2RBlHCrc1MrLyUzU',
          defaultMessage: 'Take-off:',
          description: 'Flight sorting title label for take-off time sorting',
        }),
        sortingLabel: intl.formatMessage({
          id: 'op-kBWTBodewNbHLkeSz',
          defaultMessage: 'Latest',
          description: 'Flight sorting label for the latest take-off time option',
        }),
      }
    case FlightSortOptions.EarliestLanding:
      return {
        sortingTitle: intl.formatMessage({
          id: '1ehVVvXFr_6Gk-_qwQba',
          defaultMessage: 'Landing:',
          description: 'Flight sorting title label for landing time sorting',
        }),
        sortingLabel: intl.formatMessage({
          id: 'orlzrOxOtAar3TXXh09_',
          defaultMessage: 'Earliest',
          description: 'Flight sorting label for the earliest landing time option',
        }),
      }
    default:
      return {
        sortingLabel: intl.formatMessage({
          id: 'XC2OJgViOTek1fq6djVJ',
          defaultMessage: 'Other',
          description: 'Flight sorting label for default/other sorting option',
        }),
      }
  }
}

export function getOtherSortOptions(originCode: string, destinationCode: string): Array<FlightDropdownOption<FlightSortOptions>> {
  const intl = getCurrentIntl()

  return [{
    value: FlightSortOptions.EarliestTakeOff,
    label: intl.formatMessage({
      id: 'ff_qrBHwgjezzuKrdDM3',
      defaultMessage: 'Earliest take-off ({airportCode})',
      description: 'Flight sort dropdown option for earliest take-off with airport code',
    }, { airportCode: originCode }),
  }, {
    value: FlightSortOptions.LatestTakeOff,
    label: intl.formatMessage({
      id: 'lfRlNADVmerOvxQDPraY',
      defaultMessage: 'Latest take-off ({airportCode})',
      description: 'Flight sort dropdown option for latest take-off with airport code',
    }, { airportCode: originCode }),
  }, {
    value: FlightSortOptions.EarliestLanding,
    label: intl.formatMessage({
      id: 'ZyQaj_z8NKGTnrblCGoE',
      defaultMessage: 'Earliest landing ({airportCode})',
      description: 'Flight sort dropdown option for earliest landing with airport code',
    }, { airportCode: destinationCode }),
  }, {
    value: FlightSortOptions.LatestLanding,
    label: intl.formatMessage({
      id: 'UzkDKv9RZ4JXHjcCDW8x',
      defaultMessage: 'Latest landing ({airportCode})',
      description: 'Flight sort dropdown option for latest landing with airport code',
    }, { airportCode: destinationCode }),
  }]
}

export function getFareClass(flightClass: string, intl: IntlShape) {
  return getFareClassLabel(getFareClassKey(flightClass), intl)
}

export function getFareClassKey(flightClass: string): FareClassKey {
  if (contains(flightClass, 'biz') || contains(flightClass, 'business')) {
    return fareClassBusiness
  }

  if (contains(flightClass, 'first')) {
    return fareClassFirst
  }

  if (contains(flightClass, 'premium')) {
    return fareClassPremium
  }

  return fareClassEconomy
}

export function getFareClassLabel(fareClass: FareClassKey, intl: IntlShape) {
  if (fareClass === fareClassBusiness) {
    return formatMessage(intl, messages.fareClassBusiness)
  }

  if (fareClass === fareClassFirst) {
    return formatMessage(intl, messages.fareClassFirst)
  }

  if (fareClass === fareClassPremium) {
    return formatMessage(intl, messages.fareClassPremium)
  }

  return formatMessage(intl, messages.fareClassEconomy)
}

export function getAirportDisplayText(airport?: App.AirportLocation) {
  if (!airport) {
    return undefined
  }

  const {
    cityCode,
    isMultiAirport,
    isChildDisplay,
    airportName,
    airportCode,
    cityAirportName,
  } = airport

  const isCityAirport = isMultiAirport && isChildDisplay
  const displayName = isCityAirport ? cityAirportName : airportName
  const displayCode = isCityAirport ? cityCode : airportCode

  return `${displayName} (${displayCode})`
}

export function getAirportDisplayTextDropdown(airport?: App.AirportLocation) {
  if (!airport) {
    return undefined
  }

  const {
    cityCode,
    isMultiAirport,
    isChildDisplay,
    airportName,
    airportCode,
    cityAirportName,
  } = airport

  const isCityAirport = isMultiAirport && isChildDisplay
  const displayName = isCityAirport ? cityAirportName : airportName
  const displayCode = isCityAirport ? cityCode : airportCode

  if (airport.isMultiAirport) {
    return displayName
  }

  return createElement(BodyText, { variant: 'medium' },
    displayName + ' ',
    createElement(BodyText, { variant: 'medium', colour: 'light-foreground-alt', as: 'span' }, displayCode),
  )
}

export function validateFlightOccupants(occupants: App.Occupants) {
  const totalPassengers = occupants.adults + (occupants?.children ?? 0)
  const totalInfants = occupants?.childrenAge?.filter((age) => age !== -1 && age <= maxFlightInfantAge).length ?? 0
  return totalPassengers > maxFlightPassengerCount || totalInfants > occupants.adults
}

export function mapAirportToAirportLocation(airport: App.Airport): App.AirportLocation {
  return {
    airportCode: airport.code,
    airportName: airport.name,
  }
}

export function isOneWayFlight(fareType: string): boolean {
  return !!fareType && fareType === FlightsFareTypes.ONE_WAY
}

export interface StandaloneFlightSearchParam {
  originAirportCode: string
  originAirportName: string
  destinationAirportCode: string
  destinationAirportName: string
  departDate: string
  returnDate: string | null
  adults: number
  children: number
  infants: number
  /** Comma-separated list of ages */
  childrenAge?: string
  fareClass: string
  fareType: 'return' | 'oneWay' | 'multiCity'
}

export function buildStandaloneFlightSearchUrl(params: StandaloneFlightSearchParam) {
  return `/flights-search-results?${qs.stringify(params)}`
}

export function deriveFareType(hasSecondLegWithDate: boolean, mapped: FlightsFareTypes): FlightsFareTypes {
  if (!hasSecondLegWithDate) return FlightsFareTypes.ONE_WAY
  if (mapped === FlightsFareTypes.MULTI_CITY) return FlightsFareTypes.MULTI_CITY
  return FlightsFareTypes.RETURN
}

export function getFlightDealDuration(travelPeriods: Array<App.FlightDealTravelPeriod>) {
  const earliestDate = min(travelPeriods, period => +new Date(period.departureDate))?.departureDate
  const latestDate = max(travelPeriods, period => +new Date(period.arrivalDate))?.arrivalDate

  return formatShortDateRange({ startDate: moment(earliestDate), endDate: moment(latestDate) })
}

export function filterAirportRecords(
  records: Array<App.AirportLocation>,
  excludingAirport?: App.AirportLocation,
  noParentAirports?: boolean,
) {
  if (records) {
    const excludingRecord = records.find(record => record.airportCode === excludingAirport?.airportCode)
    const airportsWithoutSelections = without(records, excludingRecord)
    const groupedAirportRecords = groupBy(airportsWithoutSelections, record => record?.cityCode)

    if (noParentAirports) {
      return airportsWithoutSelections.filter(airport => !airport.isMultiAirport)
    }

    return nonNullable(Array.from(groupedAirportRecords.entries()).map(groupedAirports =>
      groupedAirports[1].map((airport, index) => {
        if (!airport) return
        return {
          ...airport,
          isCityAirport: index !== 0 && !!groupedAirports[1].find(airport => airport?.isMultiAirport),
        }
      }),
    ).flat())
  }
  return []
}

export function isJourneyV2Items(items: Array<App.AnyJourney>): items is Array<App.JourneyV2> {
  return isJourneyV2(items?.[0])
}

export function isJourneyV2(item?: App.AnyJourney): item is App.JourneyV2 {
  return item?.itemType === JOURNEY_TYPE_V2
}

export function isJourneyV2ReturnFares(item?: App.AnyJourney) {
  return isJourneyV2(item) && item.fareType === FlightViewTypes.RETURN
}

export function isJourneyV2ReturnFaresItems(items: Array<App.AnyJourney>) {
  return items[0]?.fareType === FlightViewTypes.RETURN
}
export const JOURNEY_TYPE_V2 = 'journeyV2'

export function getMainAirlineLogo(carrierLogos?: string) {
  return carrierLogos?.split('|')?.[0]
}

export function filterSameDayFares(outboundFare: App.JourneyV2, inboundFares: Array<App.JourneyV2>) {
  const lastOutboundFlight = last(outboundFare.flightGroup.flights)
  // 5 Hours is for ticketing time and change overtime after testing it would become 3
  const arrivalTime = moment(lastOutboundFlight.arrivalTime, SHORT_TIME_FORMAT_24_HOUR).add(5, 'hours')

  return inboundFares.filter((fare) => {
    const firstInboundFlight = fare.flightGroup.flights[0]
    const departingTime = moment(firstInboundFlight.departingTime, SHORT_TIME_FORMAT_24_HOUR)
    return departingTime.isAfter(arrivalTime)
  })
}

export function calcTotalDisplayPrice(isDeparting: boolean, journeyPrice: number, departingFarePrice?: number) {
  if (isDeparting) return null
  return journeyPrice + (departingFarePrice ?? 0)
}

export function getJourneyV2IdKey(
  journeyId: string,
  searchId: string,
  fareFamilyId?: string,
) {
  return getObjectKey({
    journeyId,
    searchId,
    fareFamilyId,
  })
}

export function getDateChangeJourneyV2IdKey(
  reservationId: string,
  searchId: string,
  journeyId: string,
) {
  return getObjectKey({
    reservationId,
    searchId,
    journeyId,
  })
}

export function getFlightSeatsKey(
  searchId: string,
  fareIds: Array<string>,
  passengerCount: number,
  fareFamilyIds: Array<string | undefined> = [],
) {
  const serializedFareFamilyIds = fareFamilyIds.some(Boolean) ? fareFamilyIds.map(id => id ?? '').join(',') : undefined

  return getObjectKey({
    searchId,
    fareIds: fareIds.join(','),
    fareFamilyIds: serializedFareFamilyIds,
    passengers: passengerCount,
  })
}

export interface FlightPriceKey {
  numberOfAdults: number
  numberOfChildren: number
  numberOfInfants: number
  startDate: string
  endDate: string
  nights?: number
  origin?: string
  destination: string
  exactSabrePrice?: boolean
}

export function getFlightPriceKey(props: FlightPriceKey) {
  const exactSabrePriceKey = props.exactSabrePrice ? '-exact-sabre' : ''
  return `${props.numberOfAdults}-${props.numberOfChildren}-${props.numberOfInfants}-${props.startDate}-${props.endDate}-${props.nights}-${props.origin}-${props.destination}${exactSabrePriceKey}`
}

interface FlightKeyParams extends FlightSearchParams {
  currency: string
  key?: string | number
}

export function getFlightSearchLuxPlusCacheKey(key: string | number | undefined, requestLuxPlusPricing: boolean): string | number | undefined {
  if (!requestLuxPlusPricing) {
    return key
  }

  if (key === undefined || key === '') {
    return 'lux-plus'
  }

  return `${key}:lux-plus`
}

export function getFlightSearchKey(params: FlightKeyParams) {
  const flightsKeys = params.itinerary.map(it => [it.departureDate, it.departureAirport, it.arrivalAirport].join('-'))

  return [
    ...flightsKeys,
    params.currency,
    serialiseFlightsOccupancy(params.occupancies),
    params.fareClass,
    params.maxArrivalTime,
    params.minReturningDepartureTime,
    params.carriers?.join(','),
    params.fareType,
    params.viewType,
    params.key,
    params.filterAirlines?.join(','),
    params.source,
    params.charterSearchId,
    params.allowIncompleteJourneys ? 'incomplete-charter' : undefined,
    params.email,
    params.feederOriginAirportCode,
  ].join(':')
}

export function convertTimeObjectToMinutes(timeObject?: { hours: number, minutes: number }) {
  if (!timeObject) {
    return 0
  }
  return timeObject.hours * 60 + timeObject.minutes
}

export function convertTimeStringToMinutes(timeString: string) {
  const [hours, minutes] = timeString.split(':')

  return parseInt(hours, 10) * 60 + parseInt(minutes, 10)
}

export function convertMinutesToHoursAndMinutes(totalMinutes: number) {
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return { hours, minutes }
}

export function convertMinutesToTimeString(minutes: number) {
  const time = convertMinutesToHoursAndMinutes(minutes)
  const hoursStr = time.hours.toString()
  const minutesStr = time.minutes.toString()
  return `${hoursStr.padStart(2, '0')}:${minutesStr.padStart(2, '0')}`
}

export function getBaggageDescription(
  baggage: Array<Array<App.AddedBaggage>> = [],
  options: Array<App.FlightV2BaggageOption> = [],
): string | undefined {
  const allBags = baggage.flatMap(bags => bags).filter(bag => bag.count !== 0)

  if (allBags.length === 0) {
    return undefined
  }
  const dictCounts = groupBy(allBags, bag => bag.id)
  // everything using this data assumes its an object at the moment (not a map), put it back to an object
  const baggageIdWithCount = arrayToObject(dictCounts.entries(), ([key]) => key, ([_, bags]) => sum(bags, bag => bag.count))
  const optionsById = arrayToObject(options, option => option.id)
  const baggageDescriptions = Object.entries(baggageIdWithCount).map(([id, count]) => `${count}x ${optionsById[id]?.description}`)
  return baggageDescriptions.join(', ')
}

export function getAddedBaggageFromAddons(addons: Array<App.LegAddon>) {
  const intl = getCurrentIntl()
  return addons
    .filter(addon => addon.type === 'checked_baggage')
    .reduce<Record<string, number>>((acc, addon) => {
      const kiloLimit = addon.weightLimits?.find(limit => limit.unit === 'KILO')
      const description = kiloLimit ? intl.formatMessage({
        id: '-8EDSjnlRKigQlqypoW2',
        defaultMessage: '{weight}kg baggage',
        description: 'Purchased checked baggage weight shown in flight details',
      }, { weight: kiloLimit.weight }) : intl.formatMessage({
        id: '7F2U071CXsnVLh5HL9ee',
        defaultMessage: 'Extra baggage',
        description: 'Fallback label for purchased checked baggage without a kilogram weight limit',
      })

      acc[description] = (acc[description] || 0) + addon.quantity
      return acc
    }, {})
}

export function getLegInclusions(inclusions: Array<App.LegInclusion>) {
  return inclusions
    .filter(inclusion => inclusion.status === 'INCLUDED')
}

const defaultTime = {
  hours: 0,
  minutes: 0,
}

export function getFlightV2BestPriceAndTime(journeys: Array<App.JourneyV2>, sortOption: FlightSortOptions): { time: { hours: number, minutes: number }, price: number } {
  const sortedFlight = sortByAll(journeys, getDepartingSortListV2(sortOption))[0]

  return {
    price: sortedFlight?.price.all.totalFare || 0,
    time: sortedFlight?.flightGroup.totalFlightDuration || defaultTime,
  }
}

export function getCombinedJourneyBestPriceAndTime(combinedJourneys: Array<App.FlightCombinedJourney>, sortOption: FlightSortOptions): { time: { hours: number, minutes: number }, price: number, totalMinutes: number } {
  const sortedCombinedJourney = sortByAll(combinedJourneys, getCombinedJourneySortList(sortOption))[0]

  return {
    price: sortedCombinedJourney ? getCombinedJourneyPrice(sortedCombinedJourney) : 0,
    totalMinutes: sortedCombinedJourney ? getCombinedJourneyDuration(sortedCombinedJourney) : 0,
    time: sortedCombinedJourney ? getCombinedJourneyAverageDuration(sortedCombinedJourney) : defaultTime,
  }
}

export function getNextFlightFilters(name: keyof FlightsFiltersValues, value: string, filters: FlightsFiltersValues) {
  let nextFilter: FlightsFiltersValues = { ...filters }

  if (value === 'all' && Array.isArray(filters[name])) {
    nextFilter = {
      ...nextFilter,
      [name]: standaloneFlightsFiltersInitialState[name],
    }
    nextFilter = updateDirectFlightsFilterValues(nextFilter, name)
    return nextFilter
  }

  // Select-all switch/checkbox off. Must replace the list (not toggle-add ""),
  // otherwise an explicit full selection like ['QF','JQ'] becomes ['QF','JQ','']
  // and every option stays checked.
  if (value === '' && Array.isArray(filters[name])) {
    nextFilter = {
      ...nextFilter,
      [name]: [''],
    }
    nextFilter = updateDirectFlightsFilterValues(nextFilter, name)
    return nextFilter
  }

  // Explicit multi-value replace (e.g. uncheck one option while "all" is selected)
  const replacedValues = tryDecodeFilterPanelSet(value)
  if (replacedValues && Array.isArray(filters[name])) {
    nextFilter = {
      ...nextFilter,
      // Match select-all-off: a non-empty array that matches no options ⇒ none selected
      [name]: replacedValues.length > 0 ? replacedValues : [''],
    }
    nextFilter = updateDirectFlightsFilterValues(nextFilter, name)
    return nextFilter
  }

  const existingFilters = filters[name] || []
  const isCheckbox = Array.isArray(filters[name])

  if (isCheckbox) {
    const currentValues = Array.isArray(existingFilters) ? existingFilters : []
    if (!currentValues.includes(value)) {
      // Selecting a real option clears the select-all-off sentinel ('').
      const withoutSelectAllOff = currentValues.filter(item => item !== '')
      nextFilter = {
        ...nextFilter,
        [name]: [...withoutSelectAllOff, value],
      }
    } else if (Array.isArray(existingFilters)) {
      nextFilter = {
        ...nextFilter,
        [name]: without(existingFilters, value),
      }
    }

    nextFilter = updateDirectFlightsFilterValues(nextFilter, name)
    return nextFilter
  }

  return {
    ...nextFilter,
    [name]: value,
  }
}

const updateDirectFlightsFilterValues = (filters: FlightsFiltersValues, targetName: string): FlightsFiltersValues => {
  const nextFilter: FlightsFiltersValues = { ...filters }

  if (targetName === 'recommended') {
    if (nextFilter.recommended?.includes(FlightsFiltersRecommendedType.DIRECT_FLIGHTS)) {
      nextFilter.stops = ['0']
    } else {
      nextFilter.stops = []
    }
  }

  if (targetName === 'stops' && Array.isArray(nextFilter.recommended)) {
    if (nextFilter.stops?.length === 1 && nextFilter.stops?.includes('0')) {
      nextFilter.recommended = [...new Set([...(nextFilter.recommended || []), FlightsFiltersRecommendedType.DIRECT_FLIGHTS])]
    } else {
      nextFilter.recommended = nextFilter.recommended?.filter(filter => filter !== FlightsFiltersRecommendedType.DIRECT_FLIGHTS) || []
    }
  }

  return nextFilter
}

export function getFlightV2BestPrice(journeys: Array<App.JourneyV2>, requiredSeats?: number, isPerAdultDisplay = true): number | undefined {
  const sortedFlight = sortByAll(journeys, getDepartingSortListV2(FlightSortOptions.Cheapest))[0]
  if (!sortedFlight) {
    return undefined
  }

  const price = getEffectiveFlightPrice(sortedFlight, requiredSeats)
  return isPerAdultDisplay ? price.adult.totalFare : price.all.totalFare
}

export function isFnfPriceActive(
  journey: App.JourneyV2,
  requiredSeats?: number,
): boolean {
  if (!config.FLIGHTS_CHARTER_FRIENDS_AND_FAMILY_ENABLED) return false

  if (requiredSeats === undefined) {
    return false
  }

  const fnfEligible = journey.fnfEligible
  const minFnfSeats = Math.min(
    ...journey.flightGroup.flights.map(flight => flight.fnfSeatsLeft ?? 0),
  )
  const hasSufficientFnfInventory = requiredSeats != null && requiredSeats > 0
    ? minFnfSeats >= requiredSeats
    : minFnfSeats > 0
  return !!(fnfEligible && journey.fnfPrice && hasSufficientFnfInventory)
}

// If requiredSeats is not provided the default price will always be returned
export function getEffectiveFlightPrice(
  journey: App.JourneyV2,
  requiredSeats?: number,
): App.JourneyV2['price'] {
  if (isFnfPriceActive(journey, requiredSeats)) {
    return journey.fnfPrice as App.JourneyV2['price']
  }

  if (journey.promotionPrice) {
    return journey.promotionPrice
  }

  return journey.price
}

function getJourneyV2PassengerTypeTotalFare(priceBreakdown: App.PriceBreakdown | undefined) {
  if (priceBreakdown?.totalFare == null) {
    return 0
  }

  return priceBreakdown.totalFare * (priceBreakdown.passengerTypeQuantity?.quantity ?? 0)
}

function getJourneyV2PassengerTypeQuantity(priceBreakdown: App.PriceBreakdown | undefined) {
  if (priceBreakdown?.totalFare == null) {
    return 0
  }

  return priceBreakdown?.passengerTypeQuantity?.quantity ?? 0
}

export function getJourneyV2AveragePricePerTraveller(journey: App.JourneyV2): number {
  const passengerTypePrices = [
    journey.price.adult,
    journey.price.child,
    journey.price.infant,
  ]

  const totalFare = sum(passengerTypePrices.map(getJourneyV2PassengerTypeTotalFare))
  const totalTravellers = sum(passengerTypePrices.map(getJourneyV2PassengerTypeQuantity))

  if (totalTravellers === 0) {
    return journey.price.adult.totalFare ?? 0
  }

  return totalFare / totalTravellers
}

export function getFlightV2BestTime(journeys: Array<App.JourneyV2>): App.FlightGroup['totalFlightDuration'] | undefined {
  const sortedFlight = sortByAll(journeys, getDepartingSortListV2(FlightSortOptions.Fastest))[0]

  return sortedFlight?.flightGroup.totalFlightDuration
}

export function isSameFlightTime(timeA?: App.FlightGroup['totalFlightDuration'], timeB?: App.FlightGroup['totalFlightDuration']): boolean {
  return timeA?.hours === timeB?.hours && timeA?.minutes === timeB?.minutes
}

interface CheckoutFlightQueryParams {
  departDate?: string | null
  returnDate?: string | null
  originAirportCode?: string | null
  destinationAirportCode?: string | null
}

export function getFlightCheckoutQueryParams(url: string = ''): CheckoutFlightQueryParams | undefined {
  const pageParams = new URLSearchParams(window.location.search)
  const query = new URLSearchParams(url.split('?')[1])

  const departDate = query.get('departDate') || pageParams.get('departDate')
  const returnDate = query.get('returnDate') || pageParams.get('returnDate')
  const originAirportCode = query.get('originAirportCode') || pageParams.get('originAirportCode')
  const destinationAirportCode = query.get('destinationAirportCode') || pageParams.get('destinationAirportCode')
  if (departDate && returnDate && originAirportCode && destinationAirportCode) {
    return { departDate, destinationAirportCode, originAirportCode, returnDate }
  }
}

export function getFlightCheckoutQueryParamsFromFlightItem(item: App.Checkout.FlightItem): CheckoutFlightQueryParams | undefined {
  const {
    travelStart: departDate,
    travelEnd: returnDate,
    originAirportCode,
    destinationAirportCode,
  } = item
  if (departDate && returnDate && originAirportCode && destinationAirportCode) {
    return { departDate, destinationAirportCode, originAirportCode, returnDate }
  }
}

const FLIGHTS_SESSION_LAST_SEARCH_KEY = 'flight-session-last-search'
const FLIGHTS_RECENT_SEARCHES_KEY = 'flight-session-recent-searches'

export interface LastSearchSessionData {
  flights: Array<{
    departureAirport?: App.AirportLocation
    arrivalAirport?: App.AirportLocation
    departingDate?: string | null
    returningDate?: string | null
  }>
  fareType?: string
  fareClass?: string
  occupants?: App.Occupants
}

interface LastSearchSessionDataResponse extends LastSearchSessionData {
  searchedOn: number
}

export function setFlightSessionRecentSearch(data: LastSearchSessionData) {
  const withTimestamp = { ...data, searchedOn: Date.now() }
  isomorphicLocalStorage.set(FLIGHTS_SESSION_LAST_SEARCH_KEY, withTimestamp)

  const recent = getLocalRecentFlightSearches()
  const mapped = mapLastSearchSessionToRecent(withTimestamp)
  const seenKeys = new Set<string>()
  const next = [mapped, ...recent]
    .filter(s => {
      const k = recentSearchKey(s)
      if (seenKeys.has(k)) return false
      seenKeys.add(k)
      return true
    })
    .slice(0, 3)

  isomorphicLocalStorage.set(FLIGHTS_RECENT_SEARCHES_KEY, next)
}

export function getFlightsRecentSearch(): LastSearchSessionDataResponse | undefined {
  const recentSearch = isomorphicLocalStorage.get(FLIGHTS_SESSION_LAST_SEARCH_KEY)
  if (recentSearch) return recentSearch

  return undefined
}

function mapLastSearchSessionToRecent(last: LastSearchSessionDataResponse): App.RecentFlightSearch {
  const fareType = (last.fareType || '').toLowerCase()
  const tripType = fareType === FlightsFareTypes.ONE_WAY
    ? 'OneWay'
    : fareType === FlightsFareTypes.MULTI_CITY
      ? 'MultiCity'
      : 'Return'

  const baseLegs: Array<App.RecentFlightSearchItineraryLeg> = (last.flights || []).map(f => ({
    departureAirport: f.departureAirport?.airportCode || '',
    departureDate: f.departingDate ? moment(f.departingDate).format('DD/MM/YYYY') : '',
    arrivalAirport: f.arrivalAirport?.airportCode || '',
    departureAirportName: f.departureAirport?.airportName || '',
    arrivalAirportName: f.arrivalAirport?.airportName || '',
    departureCityDesc: '',
    arrivalCityDesc: '',
  }))

  let itinerary: Array<App.RecentFlightSearchItineraryLeg> = baseLegs

  // For return fares, append a synthetic return leg using returningDate if available
  if (tripType === 'Return' && last.flights?.[0]?.returningDate) {
    const first = last.flights[0]
    itinerary = [
      baseLegs[0],
      {
        departureAirport: first.arrivalAirport?.airportCode || '',
        departureDate: moment(first.returningDate).format('DD/MM/YYYY'),
        arrivalAirport: first.departureAirport?.airportCode || '',
        departureAirportName: first.arrivalAirport?.airportName || '',
        arrivalAirportName: first.departureAirport?.airportName || '',
        departureCityDesc: '',
        arrivalCityDesc: '',
      },
    ]
  }

  return {
    maxArrivalTime: undefined,
    minReturningDepartureTime: undefined,
    source: 'local',
    fareClass: last.fareClass,
    passengers: {
      numAdults: last.occupants?.adults || 1,
      numChildren: last.occupants?.children || 0,
      numInfants: last.occupants?.infants || 0,
    },
    tripType,
    sessionId: String(last.searchedOn || Date.now()),
    searchId: String(last.searchedOn || Date.now()),
    itinerary,
  }
}

function recentSearchKey(s: App.RecentFlightSearch): string {
  return [
    s.tripType,
    s.fareClass,
    s.passengers.numAdults,
    s.passengers.numChildren,
    s.passengers.numInfants,
    ...s.itinerary.map(it => `${it.departureAirport}-${it.arrivalAirport}-${it.departureDate}`),
  ].join('|')
}

export function getLocalRecentFlightSearches(): Array<App.RecentFlightSearch> {
  const recent = isomorphicLocalStorage.get(FLIGHTS_RECENT_SEARCHES_KEY)
  return Array.isArray(recent) ? recent : []
}

export function bumpRecentFlightSearch(search: App.RecentFlightSearch) {
  try {
    const current = getLocalRecentFlightSearches()
    const key = recentSearchKey(search)
    const deduped = current.filter(s => recentSearchKey(s) !== key)
    const next = [search, ...deduped].slice(0, 3)
    isomorphicLocalStorage.set(FLIGHTS_RECENT_SEARCHES_KEY, next)
  } catch {
    /* noop */
  }
}

export function getCartFlightItemsIds(cartItems: Array<App.Checkout.AnyItem> = []): Array<string> {
  return cartItems.reduce((acc: Array<string>, item) => {
    if (item.itemType === 'flight') {
      return [...acc, ...item.flights.map(flight => flight.journeyKey)].filter(Boolean)
    }

    return acc
  }, [])
}

export function isDepartingSegment(segments: Array<string>, activeSegment?: string) {
  if (activeSegment && segments[0]) {
    return activeSegment === segments[0]
  }

  return false
}

export function isReturningSegment(segments: Array<string>, activeSegment?: string) {
  if (activeSegment && segments[1]) {
    return activeSegment === segments[1]
  }

  return false
}

export function serialiseFlightsOccupancy(occupants: Array<App.Occupants>): Array<string> {
  return occupants.map(occupant => {
    const occupancy = [(occupant.adults || 0).toString(), (occupant.children || 0).toString(), (occupant.infants || 0).toString()]

    if (occupant.childrenAge?.length) {
      occupancy.push(`${occupant.childrenAge.join(',')}`)
    }

    return occupancy.join('-')
  })
}

export function getFlightSearchQueryparams(items: Array<FlightSearchWidgetFlightItem>, fareType: FlightsFareTypes) {
  if (fareType === FlightsFareTypes.MULTI_CITY && items.length > 1) {
    return {
      flights: items.map(item => {
        const departDate = moment(item.checkinDate).format(ISO_DATE_FORMAT)

        return {
          originAirportCode: item.departureAirport?.airportCode,
          originAirportName: item.departureAirport?.airportName,
          destinationAirportCode: item.arrivalAirport?.airportCode,
          destinationAirportName: item.arrivalAirport?.airportName,
          departDate,
        }
      }),
    }
  }

  const flight = items[0]

  const isReturnFare = fareType === FlightsFareTypes.RETURN
  const departDate = moment(flight.checkinDate).format(ISO_DATE_FORMAT)
  const returnDate = isReturnFare ? moment(flight.checkoutDate).format(ISO_DATE_FORMAT) : null

  return {
    originAirportCode: flight.departureAirport?.airportCode,
    originAirportName: flight.departureAirport?.airportName,
    destinationAirportCode: flight.arrivalAirport?.airportCode,
    destinationAirportName: flight.arrivalAirport?.airportName,
    departingQuotedPrice: flight.checkinQuotedPrice,
    returningQuotedPrice: flight.checkoutQuotedPrice,
    departDate,
    returnDate,
  }
}

export function isStandaloneFlightsCart(items: Array<App.Checkout.AnyItem>) {
  const hasFlight = items.some(isFlightItem)
  const hasBookingProtection = items.some(isInsuranceBookingProtectionItem)
  const onlyFlights = items.length === 1 && hasFlight
  const flightsAndBooking = items.length === 2 && hasFlight && hasBookingProtection
  return onlyFlights || flightsAndBooking
}

type CarrierDisplayInfo = {
  carrierCode?: string
  carrierLogo: string
  carrierName: string
  carrierBrandMark?: string
}

type OperatingAirlineDisplayInfo = {
  code: string
  name: string
  logoUrl: string
  brandMarkUrl?: string
}

/**
 * FLY-5806: only swap tile logos for A1 (APG/GSA) marketed / Z0 (Norse Atlantic UK)
 * operated journeys. Other operated-by pairs keep the marketing carrier brandmark.
 * Matching is by stable IATA code, not display name.
 */
function getDisplayCarrierForSingleOperator(
  operatingAirlines: Array<OperatingAirlineDisplayInfo>,
  marketingCarrier: CarrierDisplayInfo,
): CarrierDisplayInfo {
  if (marketingCarrier.carrierCode !== AIRLINE_CODE_APG) {
    return marketingCarrier
  }

  const operatingCarrier = operatingAirlines.find(airline => airline.code === AIRLINE_CODE_NORSE_ATLANTIC_UK)
  if (!operatingCarrier) {
    return marketingCarrier
  }

  return {
    carrierCode: marketingCarrier.carrierCode,
    carrierLogo: operatingCarrier.logoUrl || marketingCarrier.carrierLogo,
    carrierName: operatingCarrier.name || marketingCarrier.carrierName,
    carrierBrandMark: operatingCarrier.brandMarkUrl || marketingCarrier.carrierBrandMark,
  }
}

export function getCarrierInfoFromLegs(flights: Array<App.LegFlight>): {
  message?: string
  carrierLogo: string
  carrierName: string
  carrierBrandMark?: string
} {
  const operatingAirlines = flights.map(flight => flight.operatingAirline)

  const marketingCarrier = {
    carrierCode: flights[0]?.marketingAirline.code,
    carrierLogo: flights[0]?.marketingAirline.logoUrl,
    carrierName: flights[0]?.marketingAirline.name,
    carrierBrandMark: flights[0]?.marketingAirline.brandMarkUrl,
  }

  return getOperatedByMessage(operatingAirlines, marketingCarrier)
}

export function getCarrierInfoFromJourney(journey: App.JourneyV2): {
  message: string
  carrierLogo: string
  carrierName: string
  carrierBrandMark?: string
} {
  const flights = journey.flightGroup.flights
  const operatingAirlines = flights.map(flight => flight.operatingAirline)
  const codeShareDetails = flights.map(flight => flight.codeShareDetails).filter((detail): detail is string => Boolean(detail))

  const marketingCarrier = {
    carrierCode: journey.carrier || flights[0]?.marketingAirline.code,
    carrierLogo: journey.carrierLogo,
    carrierName: journey.carrierName,
    carrierBrandMark: journey.carrierBrandMark,
  }

  return getOperatedByMessage(operatingAirlines, marketingCarrier, codeShareDetails)
}

function getOperatedByMessage(
  operatingAirlines: Array<OperatingAirlineDisplayInfo>,
  marketingCarrier: CarrierDisplayInfo,
  codeShareDetails?: Array<string>,
): { message: string } & CarrierDisplayInfo {
  const intl = getCurrentIntl()
  const operatingAirlineNames = operatingAirlines.map(airline => airline.name)
  const uniqueOperatingAirlines = new Set<string>(operatingAirlineNames)
  const noMarketingAirlines = new Set<string>(operatingAirlineNames.filter(airline => airline !== marketingCarrier.carrierName))
  const noMarketingAirlinesNames = noMarketingAirlines.values()
  const uniqueCodeShareDetails = new Set<string>(codeShareDetails)
  const displayCarrier = getDisplayCarrierForSingleOperator(operatingAirlines, marketingCarrier)

  const message = marketingCarrier.carrierName

  if (uniqueOperatingAirlines.size === 1 && uniqueCodeShareDetails?.size) {
    return {
      message: intl.formatMessage(
        {
          id: 'IZ0IoszeIkB-9b5y2k3s',
          defaultMessage: '{message} · {codeShareDetails}',
          description: 'Flight carrier message with code share details in the flights section',
        },
        {
          message,
          codeShareDetails: uniqueCodeShareDetails.values().next().value,
        },
      ),
      ...(noMarketingAirlines.size === 1 ? displayCarrier : marketingCarrier),
    }
  }

  if (uniqueOperatingAirlines.size === 1 && noMarketingAirlines.size === 1) {
    return {
      message: intl.formatMessage(
        {
          id: '1p24VW6Yh-qlb98yUZQk',
          defaultMessage: '{message} · Operated by {airlineName}',
          description: 'Flight carrier message with operated by airline in the flights section',
        },
        {
          message,
          airlineName: noMarketingAirlinesNames.next().value,
        },
      ),
      ...displayCarrier,
    }
  }

  if (uniqueOperatingAirlines.size === 2 && noMarketingAirlines.size === 1) {
    return {
      message: intl.formatMessage(
        {
          id: 'SOF16ERYaWi4KiGCGW33',
          defaultMessage: '{message} · Partially operated {airlineName}',
          description: 'Flight carrier message with partially operated airline',
        },
        {
          message,
          airlineName: noMarketingAirlinesNames.next().value,
        },
      ),
      ...marketingCarrier,
    }
  }
  if (noMarketingAirlines.size >= 2) {
    return {
      message: intl.formatMessage(
        {
          id: 'XSp2h9xbu0llzURquo3u',
          defaultMessage: '{message} · Operated by multiple airlines',
          description: 'Flight carrier message when multiple operating airlines are involved',
        },
        { message },
      ),
      ...marketingCarrier,
    }
  }

  return {
    message,
    ...marketingCarrier,
  }
}

export function isLegFlight(flight?: App.LegFlight | App.FlightV2): flight is App.LegFlight {
  return !!flight && 'flightUniqueId' in flight
}

export const SPECIAL_DESTINATION_NAMES = {
  KL: 'Kuala Lumpur',
  Rio: 'Rio de Janeiro',
} as const

type SpecialDestinationKey = keyof typeof SPECIAL_DESTINATION_NAMES

export const adjustDestinationName = (destinationName: string): string => {
  const adjustedName = destinationName.replace(' All Airports', '')
  return (SPECIAL_DESTINATION_NAMES[adjustedName as SpecialDestinationKey] || adjustedName)
}

function getDomesticLoyaltyProductType(fareClass: string) {
  if (fareClass === 'business') return 'dom_business'
  return 'dom_economy'
}

function getInternationalLoyaltyProductType(fareClass: string): App.LoyaltyFlightProductType {
  if (fareClass === 'economy') return 'int_economy'
  if (fareClass === 'premium' || fareClass === 'premium economy') return 'int_premium_economy'
  if (fareClass === 'business') return 'int_business'
  if (fareClass === 'first') return 'int_first'
  return 'int_economy'
}

export function getFlightLoyaltyProductType(fareClass: string, domestic: boolean): App.LoyaltyFlightProductType {
  if (domestic) return getDomesticLoyaltyProductType(fareClass)
  return getInternationalLoyaltyProductType(fareClass)
}

export function getFlightLoyaltyProductTypeRank(type: App.LoyaltyFlightProductType): number {
  if (type === 'dom_business' || type === 'int_business') return 1
  if (type === 'int_first') return 2
  if (type === 'int_premium_economy') return 3
  return 4
}

export function isFlightJourneyDomestic(flights: Array<App.FlightV2>) {
  const country = flights[0]?.departureCountry
  return flights.every((flight) => flight.departureCountry === flight.arrivalCountry && flight.departureCountry === country)
}

/** Both legs' flights combined, in departure-then-return order. */
export function getCombinedJourneyFlights(combinedJourney: App.FlightCombinedJourney): Array<App.FlightV2> {
  return [
    ...combinedJourney.departingJourney.flightGroup.flights,
    ...combinedJourney.returningJourney.flightGroup.flights,
  ]
}

export function getTitleForFareType(fareType: `${FlightsFareTypes}`): string {
  const intl = getCurrentIntl()
  switch (fareType) {
    case FlightsFareTypes.ONE_WAY:
      return intl.formatMessage({
        id: 'If-4GG7XSY013US8X8v1',
        defaultMessage: 'One-way flight',
        description: 'Title for one-way flight fare type in checkout and cart',
      })
    case FlightsFareTypes.RETURN:
      return intl.formatMessage({
        id: 'E88JTQySMcclkETws2yC',
        defaultMessage: 'Return flights',
        description: 'Title for return flight fare type in checkout and cart',
      })
    case FlightsFareTypes.MULTI_CITY:
      return intl.formatMessage({
        id: 'nEqJ6DvpL-_lwQDBUraf',
        defaultMessage: 'Multi-city flights',
        description: 'Title for multi-city flight fare type in checkout and cart',
      })
    default:
      return fareType
  }
}

export function isCheckedBaggageIncluded(journey?: App.JourneyV2) {
  return journey?.flightGroup.flights[0].includedCheckedBaggage?.includedInFare ||
    journey?.fareFamily?.fareFeatures.find(feature => feature.type === 'checked_baggage')?.status === 'INCLUDED'
}

export function isCarryOnBaggageIncluded(journey?: App.JourneyV2) {
  return journey?.flightGroup.flights[0].includedCarryOnBaggage?.includedInFare ||
    journey?.fareFamily?.fareFeatures.find(feature => ['carry_on_bag', 'carry_on_baggage'].includes(feature.type || ''))?.status === 'INCLUDED'
}

export function getFlightsTotal(item: App.Checkout.FlightItem) {
  if (item.flights[0].fareFamily) {
    const isReturnFares = item.viewType === FlightViewTypes.TWO_ONE_WAYS_AND_RETURN

    if (isReturnFares) {
      const [departing, returning] = item.flights
      if (departing && returning) {
        return (departing.fareFamily?.cost?.all ?? 0) + (returning.fareFamily?.cost?.all ?? 0)
      }
      return departing?.fareFamily?.cost?.all ?? 0
    }

    return item.flights.reduce((sum, flight) => sum + (flight.fareFamily?.cost?.all ?? 0), 0)
  }

  return item.flights.reduce((sum, flight) => sum + (flight.cost ?? 0), 0)
}

/**
 * Public (non-member) whole-item total for a flight cart item, or `undefined` when the item
 * carries no LuxPlus+ fare.
 *
 * `getFlightsTotal` returns `fareFamily.cost.all`, which is the LuxPlus+ MEMBER fare whenever
 * the search ran with `isLuxPlus: true` — and it does for everyone when LuxPlus+ flight pricing
 * is enabled. Checkout swaps in `regularCost` for shoppers
 * who cannot redeem benefits (`checkout/selectors/payment/flights.ts`); the cart must do the
 * same or it understates the item and the price rises on the way to checkout (FLY-6013).
 *
 * Deliberately mirrors `getFlightsTotal`'s viewType traversal so the member and public totals
 * can never come from different legs. Deliberately does NOT read `journeyFlight` or apply the
 * feeder-bundle budget snap — see `getFlightItemViewLuxPlusRegularPrice` for the checkout
 * variant that does both.
 */
export function getFlightsRegularTotal(item: App.Checkout.FlightItem): number | undefined {
  // Feeder bundles are priced against an FDA budget the checkout variant snaps to. Returning
  // undefined keeps today's behaviour rather than showing an un-budgeted fare.
  if (item.bundleBudget) return undefined

  const legRegularCost = (flight: App.Checkout.FlightItemFlight): number | undefined =>
    flight.fareFamily?.bookingInfo?.isLuxPlusFare ? flight.fareFamily.regularCost?.all : undefined

  // Deliberately `some`, not `every`, and deliberately falling back to the member cost per leg
  // below: this mirrors `getFlightItemViewLuxPlusRegularPrice`, whose gate is the same `some`
  // and whose reduce ends in `total + memberPrice`. A LuxPlus+ leg with no regular cost is
  // therefore priced at its member fare on BOTH sides, so an item where only some legs carry a
  // regular cost resolves to the same mixed total the checkout selector produces. Tightening
  // this to `every` would make the cart quote the member price alone while checkout still
  // charges that mixed total — reintroducing the FLY-6013 price rise for that sub-case.
  if (!item.flights.some(flight => legRegularCost(flight) !== undefined)) return undefined

  // A leg without a member fare contributes the same number to both totals, so a mixed item
  // still yields a comparable pair.
  const legTotal = (flight: App.Checkout.FlightItemFlight): number =>
    legRegularCost(flight) ?? flight.fareFamily?.cost?.all ?? flight.cost ?? 0

  if (item.flights[0].fareFamily && item.viewType === FlightViewTypes.TWO_ONE_WAYS_AND_RETURN) {
    const [departing, returning] = item.flights
    if (departing && returning) {
      return legTotal(departing) + legTotal(returning)
    }
    return departing ? legTotal(departing) : 0
  }

  return item.flights.reduce((sum, flight) => sum + legTotal(flight), 0)
}

/**
 * The public/member price pair a cart surface should display for a flight item.
 *
 * Keeps the feature-flag gate and the fallback rule in one place for the two cart metadata
 * hooks and the cart tile. The flag is injected rather than read here so `lib/flights` stays
 * free of selectors.
 *
 * Bundled items are excluded: the bundle group tiles add a flight's total to the hotel's public
 * and member totals identically, so splitting the flight needs a design decision rather than a
 * substitution.
 */
export function getFlightsCartDisplayTotals(
  item: App.Checkout.FlightItem,
  isFlightsLuxPlusPricingEnabled: boolean,
): { publicTotal: number; memberTotal: number } {
  const memberTotal = getFlightsTotal(item)
  const isBundled = !!item.bundledItemIds?.length

  // memberPrice 0 is the documented "this item has no member price" signal in ItemViewTotals.
  if (!isFlightsLuxPlusPricingEnabled || isBundled) {
    return { publicTotal: memberTotal, memberTotal: 0 }
  }

  const regularTotal = getFlightsRegularTotal(item)
  if (regularTotal === undefined) {
    return { publicTotal: memberTotal, memberTotal: 0 }
  }

  return { publicTotal: regularTotal, memberTotal }
}

function hasBaggage(fareFamily: App.FlightFareFamily) {
  return fareFamily.fareFamily.fareFeatures.some(
    (feature: App.FareFamilyFeature) =>
      feature.category === 'baggage' &&
      feature.type === 'checked_baggage' &&
      feature.status === 'INCLUDED',
  )
}

function isRefundable(fareFamily: App.FlightFareFamily) {
  return fareFamily.fareFamily.fareFeatures.some(
    (feature: App.FareFamilyFeature) =>
      feature.category === 'fare_rules' &&
      feature.type === 'refunds' &&
      feature.status === 'INCLUDED',
  )
}

interface CheapestEconomyFareFamilyWithBaggageReturn {
  cheapestEconomyWithBaggage: App.FlightFareFamily | null
  pairedReturningFareFamily: App.FlightFareFamily | null
  journeyTotalCost: number
}

export function isFareFamilyInsuranceEligible(fareFamily: App.FlightFareFamily) {
  return hasBaggage(fareFamily) && !isRefundable(fareFamily) && fareFamily.cabin.toLowerCase() === FlightsClassTypes.ECONOMY
}

export function getCheapestEconomyFareFamilyWithBaggage(fareFamilies: Array<App.FlightFareFamily>, isReturnType: boolean = false): CheapestEconomyFareFamilyWithBaggageReturn {
  if (!Array.isArray(fareFamilies)) return { cheapestEconomyWithBaggage: null, pairedReturningFareFamily: null, journeyTotalCost: 0 }
  const fareFamiliesWithBaggage = fareFamilies.filter(family => {
    const shouldInclude = isFareFamilyInsuranceEligible(family)

    if (isReturnType) {
      // Fare Families without pairedUpsellIds are filtered out in upsell modal, so we need to ignore them too here.
      return shouldInclude && Array.isArray(family.pairedUpsellIds) && family.pairedUpsellIds.length > 0
    }

    return shouldInclude
  })
  const cheapestEconomyWithBaggage = fareFamiliesWithBaggage.length ?
      fareFamiliesWithBaggage.reduce((min, curr) =>
        curr.cost.all < min.cost.all ? curr : min,
      ) :
    null

  if (!cheapestEconomyWithBaggage) return { cheapestEconomyWithBaggage: null, pairedReturningFareFamily: null, journeyTotalCost: 0 }
  if (!isReturnType) return { cheapestEconomyWithBaggage, pairedReturningFareFamily: null, journeyTotalCost: cheapestEconomyWithBaggage.cost.all }

  const pairedUpsellId = cheapestEconomyWithBaggage.pairedUpsellIds?.[0]
  const pairedReturningFareFamily = pairedUpsellId ? fareFamilies.find(family => family.id === pairedUpsellId) : null

  if (!pairedReturningFareFamily) return { cheapestEconomyWithBaggage: null, pairedReturningFareFamily: null, journeyTotalCost: 0 }

  return { cheapestEconomyWithBaggage, pairedReturningFareFamily, journeyTotalCost: cheapestEconomyWithBaggage.cost.all + pairedReturningFareFamily.cost.all }
}

export function cloneWithInsurance(family: App.FlightFareFamily, quote: App.InsuranceQuote, occupancies: App.Occupants | undefined, shouldDivideQuoteByTwo: boolean = false, idSuffix?: string, useMemberInsurancePrice: boolean = false, isFlightLuxPlusPricingEligible: boolean = false) {
  const totalPerPerson = shouldDivideQuoteByTwo ? quote.totalPerPerson / 2 : quote.totalPerPerson
  const total = shouldDivideQuoteByTwo ? quote.total / 2 : quote.total
  // Flight CFMR carries its own member discount (luxPlusFlightTotal), computed and gated
  // server-side by svc-insurance independently of the legacy `useMemberInsurancePrice` flag
  // (which only ever applied to the generic insurance discount, disabled service-wide) - so its
  // presence alone isn't proof the *viewer* should see it. isFlightLuxPlusPricingEligible is the
  // same real-membership-or-spoof-preview check (canRequestFlightLuxPlusPricing) that already
  // gates the flight fare's own LuxPlus price, so the embedded insurance can never show a member
  // discount to a non-member/non-preview-eligible viewer even if the quote happens to include one.
  const hasFlightMemberPrice = quote.luxPlusFlightTotal !== undefined
  const shouldUseMemberPrice = hasFlightMemberPrice ? isFlightLuxPlusPricingEligible : useMemberInsurancePrice
  const memberTotalPerPerson = hasFlightMemberPrice ? quote.luxPlusFlightTotalPerPerson! : quote.luxPlusTotalPerPerson
  const memberTotal = hasFlightMemberPrice ? quote.luxPlusFlightTotal! : quote.luxPlusTotal
  const luxPlusTotalPerPerson = shouldUseMemberPrice && memberTotalPerPerson > 0 ? (shouldDivideQuoteByTwo ? memberTotalPerPerson / 2 : memberTotalPerPerson) : totalPerPerson
  const luxPlusTotal = shouldUseMemberPrice && memberTotal > 0 ? (shouldDivideQuoteByTwo ? memberTotal / 2 : memberTotal) : total
  return {
    ...family,
    costWithInsurance: {
      adult: family.cost.adult + (occupancies?.adults ? totalPerPerson : 0),
      children: family.cost.children + (occupancies?.children ? totalPerPerson : 0),
      infant: family.cost.infant + (occupancies?.infants ? totalPerPerson : 0),
      all: family.cost.all + total,
    },
    luxPlusCostWithInsurance: {
      adult: family.cost.adult + (occupancies?.adults ? luxPlusTotalPerPerson : 0),
      children: family.cost.children + (occupancies?.children ? luxPlusTotalPerPerson : 0),
      infant: family.cost.infant + (occupancies?.infants ? luxPlusTotalPerPerson : 0),
      all: family.cost.all + luxPlusTotal,
    },
    withEmbeddedInsurance: true,
    id: `${family.id}${FLIGHT_INSURANCE_ID_PREFIX}${idSuffix ?? '1'}`,
  }
}

export function getProtectedRoundTripPrice(
  baseRoundTripPrice: number | undefined,
  baseFarePrice: number,
  protectedFarePrice: number | undefined,
) {
  if (baseRoundTripPrice === undefined || protectedFarePrice === undefined) {
    return protectedFarePrice
  }

  return baseRoundTripPrice + protectedFarePrice - baseFarePrice
}

export function getFeaturesByFareFamily(fareFamilies: Array<App.FlightFareFamily>) {
  const allFeatures = uniqueBy(fareFamilies.flatMap(family => family.fareFamily?.fareFeatures ?? []), feature => feature.group)

  return arrayToMap(
    fareFamilies,
    family => family.id,
    family => {
      const fareFeatures = family.fareFamily?.fareFeatures ?? []
      const featuresByCode = arrayToMap(fareFeatures, feature => feature.group)
      const featuresByFareFamily = allFeatures.map(feature => ({
        ...(featuresByCode.get(feature.group) ?? feature),
        status: featuresByCode.get(feature.group)?.status ?? 'NOT_OFFERED',
      }))

      if (family.withEmbeddedInsurance) {
        const intl = getCurrentIntl()
        const filteredFeaturesByFareFamily = featuresByFareFamily.filter(({ description }) => !description?.match(/(?:refunds?|cancellations?)/i))
        return [
          {
            status: 'INCLUDED',
            code: 'insurance',
            description: intl.formatMessage({
              id: 'Ownpe0MZbMsSL4sKfsbz',
              defaultMessage: 'No extra fees for claims',
              description: 'Fare family feature description for embedded insurance in the flight fare selection UI',
            }),
            type: 'insurance',
            group: 0,
            category: 'fare_rules',
          } as App.FareFamilyFeature,
          ...filteredFeaturesByFareFamily,
        ]
      }

      return featuresByFareFamily
    },
  )
}

export function hasCheckoutFlightItemsWithEmbeddedInsurance(flightItems: Array<App.Checkout.FlightItem>) {
  return flightItems.some(item => item.flights.some(flight => flight.fareFamily?.withEmbeddedInsurance || flight.withEmbeddedInsurance))
}

interface NameAdjustmentParams {
  firstName: string
  middleName: string
  lastName: string
  nameAdjustmentType: string | undefined
  maxTotalLength: number | undefined
}

export enum NameAdjustmentType {
  REMOVE_MIDDLE_NAME = 'REMOVE_MIDDLE_NAME',
  ABBREVIATE_MIDDLE_NAME = 'ABBREVIATE_MIDDLE_NAME',
  REMOVE_APOSTROPHE = 'REMOVE_APOSTROPHE',
  REMOVE_HYPHEN = 'REMOVE_HYPHEN',
  REMOVE_PERIOD = 'REMOVE_PERIOD',
  REPLACE_DIACRITICS = 'REPLACE_DIACRITICS',
}

interface NameAdjustmentResult {
  name: string
  adjustments: Array<NameAdjustmentType>
}

interface FullNameAdjustmentResult {
  firstName: string
  middleName: string
  lastName: string
  title?: string
  firstNameAdjustments: Array<string>
  middleNameAdjustments: Array<string>
  lastNameAdjustments: Array<string>
}

// Map of diacritics to their standard equivalents
const baseDiacriticsMap: Record<string, string> = {
  À: 'A',
  Á: 'A',
  Â: 'A',
  Ã: 'A',
  Ä: 'AE',
  Å: 'AA',
  Æ: 'AE',
  Ç: 'C',
  È: 'E',
  É: 'E',
  Ê: 'E',
  Ë: 'E',
  Ì: 'I',
  Í: 'I',
  Î: 'I',
  Ï: 'I',
  Ð: 'D',
  Ñ: 'N',
  Ò: 'O',
  Ó: 'O',
  Ô: 'O',
  Õ: 'O',
  Ö: 'OE',
  Ø: 'OE',
  Ù: 'U',
  Ú: 'U',
  Û: 'U',
  Ü: 'UE',
  Ý: 'Y',
  Þ: 'TH',
  Ā: 'A',
  Ă: 'A',
  Ą: 'A',
  Ć: 'C',
  Ĉ: 'C',
  Ċ: 'C',
  Č: 'C',
  Ď: 'D',
  Đ: 'D',
  Ē: 'E',
  Ĕ: 'E',
  Ė: 'E',
  Ę: 'E',
  Ě: 'E',
  Ĝ: 'G',
  Ğ: 'G',
  Ġ: 'G',
  Ģ: 'G',
  Ĥ: 'H',
  Ħ: 'H',
  Ĩ: 'I',
  Ī: 'I',
  Ĭ: 'I',
  Į: 'I',
  İ: 'I',
  ı: 'I',
  Ĳ: 'IJ',
  Ĵ: 'J',
  Ķ: 'K',
  Ĺ: 'L',
  Ļ: 'L',
  Ľ: 'L',
  Ŀ: 'L',
  Ł: 'L',
  Ń: 'N',
  Ņ: 'N',
  Ň: 'N',
  Ŋ: 'N',
  Ō: 'O',
  Ŏ: 'O',
  Ő: 'O',
  Œ: 'OE',
  Ŕ: 'R',
  Ŗ: 'R',
  Ř: 'R',
  Ś: 'S',
  Ŝ: 'S',
  Ş: 'S',
  Š: 'S',
  Ţ: 'T',
  Ť: 'T',
  Ŧ: 'T',
  Ũ: 'U',
  Ū: 'U',
  Ŭ: 'U',
  Ů: 'U',
  Ű: 'U',
  Ų: 'U',
  Ŵ: 'W',
  Ŷ: 'Y',
  Ÿ: 'Y',
  Ź: 'Z',
  Ż: 'Z',
  Ž: 'Z',
  ẞ: 'SS',
}

// Extend the base diacritics map with lowercase equivalents
function extendWithLowercase(map: Record<string, string>): Record<string, string> {
  const extended: Record<string, string> = { ...map }
  for (const [char, replacement] of Object.entries(map)) {
    const lowerChar = char.toLowerCase()
    if (!extended[lowerChar]) {
      extended[lowerChar] = replacement.toLowerCase()
    }
  }
  return extended
}

const diacriticsMap: Record<string, string> = extendWithLowercase(baseDiacriticsMap)

export function adjustMiddleName({
  firstName,
  middleName,
  lastName,
  nameAdjustmentType,
  maxTotalLength,
}: NameAdjustmentParams) {
  const totalLength = firstName.length + (middleName?.length || 0) + lastName.length

  if (maxTotalLength === undefined || !middleName) {
    return middleName
  }

  // We don't need to apply custom adjustments to the middle name if the total length is less than the max total length
  if (totalLength <= maxTotalLength) {
    return middleName
  }

  if (nameAdjustmentType === NameAdjustmentType.REMOVE_MIDDLE_NAME) {
    return ''
  } else if (nameAdjustmentType === NameAdjustmentType.ABBREVIATE_MIDDLE_NAME) {
    const currentTotalLengthWithoutMiddle = firstName.length + lastName.length
    const middleNames = middleName.split(' ')
    for (let i = middleNames.length - 1; i >= 0; i--) {
      middleNames[i] = middleNames[i].slice(0, 1)
      if (currentTotalLengthWithoutMiddle + middleNames.join(' ').length <= maxTotalLength) {
        break
      }
    }
    return middleNames.join(' ')
  }

  return middleName
}

export function formatFlightTravellerName(traveller: App.FlightTraveller): string {
  const flightTitleLabels = getFlightTitleLabels(getCurrentIntl())
  const title = flightTitleLabels?.[traveller.type]?.[traveller.title] ?? traveller.title ?? ''
  return `${title} ${traveller.given_name} ${traveller.surname}`.trim()
}

export function formatFlightTravellerNameWithoutTitle(traveller: App.FlightTraveller): string {
  return [traveller.given_name, traveller.middle_name, traveller.surname]
    .filter((part): part is string => Boolean(part) && part !== 'undefined')
    .join(' ')
}

export function formatFlightTravellerNameInitials(traveller: App.FlightTraveller, isFirstLetterOnly: boolean = false): string {
  if (isFirstLetterOnly) {
    return traveller.given_name.charAt(0)
  }
  return traveller.given_name.charAt(0) + traveller.surname.charAt(0)
}

export function adjustName(name: string): NameAdjustmentResult {
  const adjustments: Array<NameAdjustmentType> = []
  let adjustedName = name

  if (adjustedName.includes("'")) {
    adjustedName = adjustedName.replace(/'/g, '')
    adjustments.push(NameAdjustmentType.REMOVE_APOSTROPHE)
  }

  if (adjustedName.includes('-')) {
    adjustedName = adjustedName.replace(/-/g, ' ')
    adjustments.push(NameAdjustmentType.REMOVE_HYPHEN)
  }

  if (adjustedName.includes('.')) {
    adjustedName = adjustedName.replace(/\./g, ' ')
    adjustments.push(NameAdjustmentType.REMOVE_PERIOD)
  }

  let hasDiacritics = false
  let normalizedName = ''
  for (let i = 0; i < adjustedName.length; i++) {
    const char = adjustedName[i]
    if (diacriticsMap[char]) {
      normalizedName += diacriticsMap[char]
      hasDiacritics = true
    } else {
      normalizedName += char
    }
  }

  if (hasDiacritics) {
    adjustedName = normalizedName
    adjustments.push(NameAdjustmentType.REPLACE_DIACRITICS)
  }

  return {
    name: adjustedName,
    adjustments,
  }
}

/**
 * Adjusts a complete passenger name, normalizing special characters and handling length constraints
 *
 * @param params Object containing title, firstName, middleName, lastName, nameAdjustmentType and maxTotalLength
 * @returns Object containing adjusted name parts and their adjustment details
 */
export function adjustFullName({
  title,
  firstName,
  middleName,
  lastName,
  nameAdjustmentType,
  maxTotalLength,
}: {
  title?: string
  firstName: string
  middleName: string
  lastName: string
  nameAdjustmentType?: string
  maxTotalLength?: number
}): FullNameAdjustmentResult {
  // First normalize all name parts to handle special characters and diacritics
  const adjustedFirstName = adjustName(firstName)
  const adjustedLastName = adjustName(lastName)
  const adjustedMiddleName = adjustName(middleName)

  // Then apply the length adjustment to the middle name if needed
  const finalMiddleName = adjustMiddleName({
    firstName: adjustedFirstName.name,
    middleName: adjustedMiddleName.name,
    lastName: adjustedLastName.name,
    nameAdjustmentType,
    maxTotalLength,
  })

  // If middle name was further adjusted for length, add that to its adjustments
  const middleNameAdjustments = [...adjustedMiddleName.adjustments]
  if (finalMiddleName !== adjustedMiddleName.name) {
    if (finalMiddleName === '') {
      middleNameAdjustments.push(NameAdjustmentType.REMOVE_MIDDLE_NAME)
    } else if (finalMiddleName.length < adjustedMiddleName.name.length) {
      middleNameAdjustments.push(NameAdjustmentType.ABBREVIATE_MIDDLE_NAME)
    }
  }

  return {
    title,
    firstName: adjustedFirstName.name,
    middleName: finalMiddleName,
    lastName: adjustedLastName.name,
    firstNameAdjustments: adjustedFirstName.adjustments,
    middleNameAdjustments,
    lastNameAdjustments: adjustedLastName.adjustments,
  }
}

/**
 * Maps FlightPassenger types to FlightSeatTraveler types
 */
const mapPassengerTypeToSeatType = (type: 'adult' | 'child' | 'infant'): 'ADT' | 'CHD' | 'INF' => {
  switch (type) {
    case 'adult':
      return 'ADT'
    case 'child':
      return 'CHD'
    case 'infant':
      return 'INF'
    default:
      return 'ADT'
  }
}

/**
 * Converts FlightPassenger array to FlightSeatTraveler array
 */
export function mapPassengersToSeatTravelers(
  passengers: Array<App.Checkout.FlightPassenger>,
  travellerFormsById: Map<string, App.Checkout.TravellerForm>,
  carrierCodes: Array<string>,
): Array<FlightSeatTraveler> {
  return passengers.map(passenger => {
    const form = travellerFormsById.get(passenger.id)
    return {
      id: passenger.id,
      type: mapPassengerTypeToSeatType(passenger.type),
      givenName: form?.data.firstName,
      surname: form?.data.lastName,
      loyaltyProgramNumbers: form ?
          toSeatsLoyaltyProgramNumbers(extractCheckoutFrequentFlyers(form, carrierCodes)) :
        undefined,
    }
  })
}

/**
 * Maps FlightPassenger types to the Sabre baggage traveller codes. NOTE: baggage uses
 * 'CNN' for a child (unlike seats which use 'CHD') — see `BaggageAvailabilityTraveler`.
 */
const mapPassengerTypeToBaggageType = (type: 'adult' | 'child' | 'infant'): 'ADT' | 'CNN' | 'INF' => {
  switch (type) {
    case 'child':
      return 'CNN'
    case 'infant':
      return 'INF'
    case 'adult':
    default:
      return 'ADT'
  }
}

/**
 * Converts checkout passengers + traveller forms to the baggage availability traveller shape.
 * `firstName`/`lastName` are required by the svc-flights baggage availability schema, so they
 * are sourced from the traveller form (populated once traveller details are completed).
 */
export function mapPassengersToBaggageTravelers(
  passengers: Array<App.Checkout.FlightPassenger>,
  travellerFormsById: Map<string, App.Checkout.TravellerForm>,
): Array<BaggageAvailabilityTraveler> {
  return passengers.map(passenger => {
    const form = travellerFormsById.get(passenger.id)
    return {
      id: passenger.id,
      type: mapPassengerTypeToBaggageType(passenger.type),
      firstName: form?.data.firstName ?? '',
      lastName: form?.data.lastName ?? '',
    }
  })
}

/**
 * Fare-keyed baggage selection/availability key for a flight item. Shares the seat key so a
 * different flight (new journeyIds) yields a new key and the old baggage state falls away.
 */
export function getFlightBaggageSelectionKey(item: App.Checkout.FlightItem): string {
  const searchId = item.searchId ?? ''
  const fareIds = item.flights.map(flight => flight.journeyId)
  const fareFamilyIds = item.flights.map(flight => flight.fareFamily?.id)
  const passengerCount = item.passengers?.length ?? 0
  return getFlightSeatsKey(searchId, fareIds, passengerCount, fareFamilyIds)
}

/** This is not a good mapping, avoid using it if you can */
export function airportLocationToAirport(airportLocation: App.AirportLocation): App.Airport {
  return {
    code: airportLocation.airportCode,
    name: airportLocation.airportName,
    latitude: airportLocation.lt ?? 0,
    longitude: airportLocation.lg ?? 0,
  }
}

/**
 * Parses weight in kg from a baggage description string.
 * Returns undefined if no valid weight is found.
 * @example parseWeightInKg("7kg carry-on baggage") => 7
 * @example parseWeightInKg("Carry-on baggage") => undefined
 */
export function parseWeightInKg(description?: string | null): number | undefined {
  if (!description) {
    return undefined
  }
  const match = description.match(/(\d+(?:\.\d+)?)\s?kg/i)
  if (!match) {
    return undefined
  }
  const parsed = Number(match[1])
  return Number.isFinite(parsed) ? parsed : undefined
}

/**
 * Parses weight and count from baggage descriptions like "2x 7kg", "7kg 2x", or "7kg"
 * @returns { count: number, weightPerItemKg: number, totalWeightKg: number } or undefined if parsing fails
 */
export function parseWeightWithCount(description?: string | null): { count: number; weightPerItemKg: number; totalWeightKg: number } | undefined {
  if (!description) {
    return undefined
  }

  // Match patterns like "2x 7kg"
  const matchCountFirst = description.match(/(\d+)\s?x\s?(\d+(?:\.\d+)?)\s?kg/i)
  if (matchCountFirst) {
    const count = Number(matchCountFirst[1])
    const weightPerItem = Number(matchCountFirst[2])
    if (Number.isFinite(count) && Number.isFinite(weightPerItem)) {
      return {
        count,
        weightPerItemKg: weightPerItem,
        totalWeightKg: count * weightPerItem,
      }
    }
  }

  // Match patterns like "7kg 2x"
  const matchWeightFirst = description.match(/(\d+(?:\.\d+)?)\s?kg\s?(\d+)\s?x/i)
  if (matchWeightFirst) {
    const weightPerItem = Number(matchWeightFirst[1])
    const count = Number(matchWeightFirst[2])
    if (Number.isFinite(count) && Number.isFinite(weightPerItem)) {
      return {
        count,
        weightPerItemKg: weightPerItem,
        totalWeightKg: count * weightPerItem,
      }
    }
  }

  // Fallback to single weight without count
  const weight = parseWeightInKg(description)
  if (weight !== undefined) {
    return {
      count: 1,
      weightPerItemKg: weight,
      totalWeightKg: weight,
    }
  }

  return undefined
}

/**
 * Format couch seats as a range (e.g., "33D-E" for seats 33D and 33E)
 */
export function formatCouchSeatRange(couchSeats: Array<{ seatNumber: string }>): string {
  if (!couchSeats.length) return ''
  if (couchSeats.length === 1) return couchSeats[0].seatNumber

  const seatNumbers = couchSeats.map(s => s.seatNumber)
  const firstSeat = seatNumbers[0]
  const lastSeat = seatNumbers[seatNumbers.length - 1]

  // Extract row number and column from first and last seat
  const rowMatch = firstSeat.match(/^(\d+)([A-Z]+)$/)
  const lastColMatch = lastSeat.match(/^(\d+)([A-Z]+)$/)

  if (rowMatch && lastColMatch) {
    const row = rowMatch[1]
    const firstCol = rowMatch[2]
    const lastCol = lastColMatch[2]
    return `${row}${firstCol}-${lastCol}`
  }

  // Fallback to comma-separated if pattern doesn't match
  return seatNumbers.join(', ')
}

/**
 * Check if Economy Extra (LUXROW) fare class is compatible with the given occupancies.
 * Economy Extra supports two modes:
 *
 * 1. Single occupancy mode: 1 adult with 0 or 1 infant
 * 2. Couples mode: Economy Extra cabins seat exactly 2 passengers
 *
 * Rules for couples mode:
 * - Total seated passengers (adults + children aged 2+) must be divisible by 2
 * - At most 1 infant per adult (each infant sits on one adult's lap)
 *
 * Returns false if occupancies array is empty.
 */
export function isSkyLoungeCompatible(occupancies: Array<App.Occupants>): boolean {
  if (!occupancies.length) return false

  // Single occupancy mode: 1 adult, no children, 0 or 1 infant
  if (isSkyLoungeSingleOccupancy(occupancies)) return true

  // Calculate totals for couples mode check
  let totalAdults = 0
  let totalSeatedPassengers = 0
  let totalInfants = 0

  for (const o of occupancies) {
    totalAdults += o.adults

    const infantsFromAge = o.childrenAge?.filter((age) => age <= maxFlightInfantAge).length ?? 0
    const childrenFromAge = o.childrenAge?.filter((age) => age > maxFlightInfantAge).length ?? 0

    // When childrenAge is available, use age-based categorization to correctly
    // distinguish infants (age <= maxFlightInfantAge, lap passengers) from children needing seats
    const hasChildrenAges = (o.childrenAge?.length ?? 0) > 0
    totalInfants += hasChildrenAges ? infantsFromAge : (o.infants ?? 0)
    totalSeatedPassengers += o.adults + (hasChildrenAges ? childrenFromAge : (o.children ?? 0))
  }

  // Economy Extra couples mode is compatible if:
  // - Total seated passengers is divisible by 2 (fills cabins exactly)
  // - Infants cannot exceed adults (each infant sits on one adult's lap)
  return totalSeatedPassengers > 0 &&
    totalSeatedPassengers % 2 === 0 &&
    totalInfants <= totalAdults
}

/**
 * Checks if the occupancy qualifies for single-occupancy Economy Extra mode.
 * Returns true if exactly 1 adult with 0 or 1 infant (no children needing seats).
 */
export function isSkyLoungeSingleOccupancy(occupancies: Array<App.Occupants>): boolean {
  if (!config.ENABLE_SKYLOUNGE_SINGLE_ADULT) return false

  if (!occupancies.length) return false

  let totalAdults = 0
  let totalSeatedPassengers = 0
  let totalInfants = 0

  for (const o of occupancies) {
    totalAdults += o.adults

    const infantsFromAge = o.childrenAge?.filter((age) => age <= maxFlightInfantAge).length ?? 0
    const childrenFromAge = o.childrenAge?.filter((age) => age > maxFlightInfantAge).length ?? 0

    // When childrenAge is available, use age-based categorization to correctly
    // distinguish infants (age <= maxFlightInfantAge, lap passengers) from children needing seats
    const hasChildrenAges = (o.childrenAge?.length ?? 0) > 0
    totalInfants += hasChildrenAges ? infantsFromAge : (o.infants ?? 0)
    totalSeatedPassengers += o.adults + (hasChildrenAges ? childrenFromAge : (o.children ?? 0))
  }

  return totalAdults === 1 && totalSeatedPassengers === 1 && totalInfants <= 1
}

interface SkyLoungeInfantMappingTraveller {
  id: string
  type: string
  mapped_traveler?: string
  companionName?: string
  name?: string
}

/**
 * Maps infants to adults for Economy Extra (LUXROW) couples: up to 1 infant per adult.
 * Each adult in a couple (traveller1 and traveller2) can hold one infant on their lap.
 */
export function mapSkyLoungeInfantsToAdults(
  travellers: Array<SkyLoungeInfantMappingTraveller>,
  couplesConfiguration: App.EconomyLuxCouplesConfiguration | undefined,
): Map<string, string> {
  const mapping = new Map<string, string>()

  if (!couplesConfiguration?.couples.length) return mapping

  const infantTravellers = travellers.filter(t => t.type === 'infant')
  if (infantTravellers.length === 0) return mapping

  const couples = couplesConfiguration.couples
  const usedAdultIds = new Set<string>()
  const usedInfantIds = new Set<string>()

  // Collect all adult IDs in couples order (traveller1 before traveller2 per couple)
  const coupleAdultIds: Array<string> = []
  for (const couple of couples) {
    coupleAdultIds.push(couple.traveller1Id, couple.traveller2Id)
  }

  for (const infant of infantTravellers) {
    if (usedInfantIds.has(infant.id)) continue

    let assignedAdultId: string | undefined

    const existingMappedAdultId = infant.mapped_traveler
    const existingCompanionName = infant.companionName

    if (existingMappedAdultId) {
      if (coupleAdultIds.includes(existingMappedAdultId) && !usedAdultIds.has(existingMappedAdultId)) {
        assignedAdultId = existingMappedAdultId
      }
    } else if (existingCompanionName) {
      const matchedAdultId = coupleAdultIds.find(adultId => {
        if (usedAdultIds.has(adultId)) return false
        const adult = travellers.find(t => t.id === adultId)
        return adult?.name === existingCompanionName
      })
      if (matchedAdultId) {
        assignedAdultId = matchedAdultId
      }
    }

    if (assignedAdultId === undefined) {
      assignedAdultId = coupleAdultIds.find(id => !usedAdultIds.has(id))
    }

    if (assignedAdultId !== undefined) {
      usedAdultIds.add(assignedAdultId)
      usedInfantIds.add(infant.id)
      mapping.set(assignedAdultId, infant.id)
    }
  }

  return mapping
}

export function subtractDuration(
  total: { hours: number; minutes: number },
  subtract: { hours: number; minutes: number },
): { hours: number; minutes: number } {
  const diff = (total.hours * 60 + total.minutes) - (subtract.hours * 60 + subtract.minutes)
  const remaining = Math.max(diff, 0)
  return {
    hours: Math.floor(remaining / 60),
    minutes: remaining % 60,
  }
}

export function getFareWatchStatusKey(query: { tripType: string, fareClass: string, itineraries: Array<{ origin: string, destination: string, departureDate: string }> }): string {
  const itineraryKey = query.itineraries
    .map(it => `${it.origin}-${it.destination}-${it.departureDate}`)
    .join('|')
  return `${query.tripType}:${query.fareClass}:${itineraryKey}`
}

/**
 * Returns the number of calendar days between a flight's departure and arrival dates.
 * Flight API date fields are date-only values, so parse them in UTC to avoid timezone and DST offsets.
 */
export function getArrivalDayOffset(departingDate?: string, arrivalDate?: string): number {
  if (!departingDate || !arrivalDate) return 0

  const departureMoment = moment.utc(departingDate, ISO_DATE_FORMAT, true)
  const arrivalMoment = moment.utc(arrivalDate, ISO_DATE_FORMAT, true)

  if (!departureMoment.isValid() || !arrivalMoment.isValid()) return 0

  return Math.max(arrivalMoment.diff(departureMoment, 'days'), 0)
}
